import {
  type DropTargetDropEvent,
  type DropTargetGetFeedback,
  type DropTargetOptions,
  MenuItem as ComponentMenuItem,
  notify,
  Tooltip,
  useConfirmModal,
  useDropTarget,
} from '@affine/component';
import { DocumentRailModeTarget } from '@affine/core/blocksuite/block-suite-mode-switch';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { useBootSplashReady } from '@affine/core/bootstrap/boot-splash';
import {
  recordBootEvent,
  setBootBlocker,
} from '@affine/core/bootstrap/boot-trace';
import { useGuard } from '@affine/core/components/guard/use-guard';
import { useWorkspaceCreateDocPermission } from '@affine/core/components/guard/use-workspace-create-doc-permission';
import { useTrashFolderToast } from '@affine/core/components/hooks/affine/use-trash-folder-toast';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { AppSidebarService } from '@affine/core/modules/app-sidebar';
import {
  AddPageButton,
  // StreamLain: AppDownloadButton unused
  // AppDownloadButton,
  AppSidebar,
  MenuItem,
  MenuLinkItem,
  QuickSearchInput,
  SidebarConnectivityStatus,
  SidebarReorderableItem,
  SidebarReorderableList,
  SidebarScrollableContainer,
} from '@affine/core/modules/app-sidebar/views';
import {
  AuthService,
  GraphQLService,
  ServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import {
  createDefaultDocCreateConfig,
  type DocCreateConfig,
} from '@affine/core/modules/doc-share';
import { DocSwitcherService } from '@affine/core/modules/doc-switcher';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import {
  FolderApiService,
  FolderMutationOutboxService,
  type FolderPermissions,
  getRememberedFolderPermissions,
  moveFolderToTrashLocalFirst as moveFolderToTrashLocalFirstAction,
  WorkspaceAreaCacheStore,
  type WorkspaceAreaRef,
} from '@affine/core/modules/folder-api';
import {
  createDefaultFolderCreateConfig,
  type FolderCreateConfig,
  FolderShareModal,
} from '@affine/core/modules/folder-share';
import { journalFolderId, JournalService } from '@affine/core/modules/journal';
import {
  NavigationPanelRevealService,
  NavigationPanelService,
} from '@affine/core/modules/navigation-panel';
import { NotificationCountService } from '@affine/core/modules/notification';
import {
  AccessibleFolderCacheStore,
  type AccessibleFolderMeta,
  type AccessibleTrashedFolderMeta,
  FolderStore,
  OrganizeService,
} from '@affine/core/modules/organize';
import {
  ObjectPermissionMutationService,
  WorkspacePermissionService,
} from '@affine/core/modules/permissions';
import { CMDKQuickSearchService } from '@affine/core/modules/quicksearch/services/cmdk';
import { WorkspaceShareSettingService } from '@affine/core/modules/share-setting';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { StreamLainCustomAppIcon } from '@affine/core/modules/streamlain/components/custom-app-icon';
import {
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  FEATURE_KEY_DATABASE_CALENDAR,
  FEATURE_KEY_PRIVATE_FOLDERS,
  FEATURE_KEY_WORKFLOW,
  getWorkspaceMailMissingFeature,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useWorkspaceCustomApps } from '@affine/core/modules/streamlain/hooks/use-workspace-custom-apps';
import { useWorkspaceLaunchableCustomApps } from '@affine/core/modules/streamlain/hooks/use-workspace-launchable-custom-apps';
import { getMailAppLabel } from '@affine/core/modules/streamlain/mail/mail-app-label';
import { useHiddenMailFolderDocIds } from '@affine/core/modules/streamlain/mail/mail-folder-visibility-filter';
import { useWorkspaceMailAccounts } from '@affine/core/modules/streamlain/mail/use-workspace-mail';
import { requestNativeWorkspaceAppSidebar } from '@affine/core/modules/streamlain/native-app';
import { WorkflowService } from '@affine/core/modules/streamlain/services/workflow';
import { WorkflowLivePresenceService } from '@affine/core/modules/streamlain/services/workflow-live-presence';
import {
  type Workspace,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import {
  deleteCustomAppMutation,
  FolderRole,
  mailFoldersQuery,
  Permission,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import type { Store } from '@blocksuite/affine/store';
import {
  AccountIcon,
  AddOrganizeIcon,
  AllDocsIcon,
  DeleteIcon,
  EmailIcon,
  FavoriteIcon,
  FolderIcon,
  ImportIcon,
  LockIcon,
  NotificationIcon,
  PageIcon,
  PublishIcon,
  SettingsIcon,
  SidebarIcon,
  TeamWorkspaceIcon,
  TodayIcon,
} from '@blocksuite/icons/rc';
import {
  LiveData,
  useLiveData,
  useService,
  useServiceOptional,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import type {
  HTMLAttributes,
  JSX,
  ReactElement,
  ReactNode,
  Ref,
  RefObject,
  SVGAttributes,
  SVGProps,
} from 'react';
import {
  cloneElement,
  createContext,
  forwardRef,
  Fragment,
  lazy,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';

import {
  CollapsibleSection,
  NavigationPanelCollections,
  NavigationPanelFavorites,
  NavigationPanelMigrationFavorites,
  NavigationPanelOrganize,
  NavigationPanelTags,
} from '../../desktop/components/navigation-panel';
import { NavigationPanelEmptySection } from '../../desktop/components/navigation-panel/layouts/empty-section';
import { NavigationPanelDocNode } from '../../desktop/components/navigation-panel/nodes/doc';
import { NavigationPanelFolderNode } from '../../desktop/components/navigation-panel/nodes/folder';
import { isFolderKnownEmpty } from '../../desktop/components/navigation-panel/nodes/folder/children-state';
import { FolderEmpty } from '../../desktop/components/navigation-panel/nodes/folder/empty';
import { OrganizeCreateMenu } from '../../desktop/components/navigation-panel/sections/organize/create-menu';
import { organizeChildrenDropEffect } from '../../desktop/components/navigation-panel/sections/organize/dnd';
import {
  forEachNavigationPanelDragSelectionItem,
  getNavigationPanelDragSelection,
  NavigationPanelTreeNode,
  type NavigationPanelTreeNodeDropEffect,
  NavigationPanelTreeRoot,
  NavigationPanelTreeSelectionContext,
  type NavigationPanelTreeSelectionItem,
  NavigationPanelTreeSelectionProvider,
  NavigationPanelTreeSelectionToolbar,
} from '../../desktop/components/navigation-panel/tree';
import { NavigationPanelTreeNodeInlineRename } from '../../desktop/components/navigation-panel/tree/inline-rename';
import { canCompleteNavigationPanelReveal } from '../../desktop/components/navigation-panel/tree/rename-intent';
import { rootDropZone } from '../../desktop/components/navigation-panel/tree/root-drop-zone.css';
import { SidebarAreaHeaderActions } from '../../desktop/components/navigation-panel/tree/sidebar-area-header-actions';
import type { SidebarAreaReorderActions } from '../../desktop/components/navigation-panel/tree/sidebar-area-options-menu';
import {
  NavigationPanelSidebarSortModeProvider,
  useNavigationPanelSidebarSortMode,
} from '../../desktop/components/navigation-panel/tree/sidebar-sort-context';
import {
  compareSidebarManualOrderItems,
  sidebarManualOrderKey,
  type SidebarManualOrderPosition,
  useSidebarManualOrder,
} from '../../desktop/components/navigation-panel/tree/use-sidebar-manual-order';
import { buildSidebarCreateActions } from '../../modules/app-sidebar/views/area-actions/create-actions';
import { SIDEBAR_MENU_ITEM_ICON_SIZE } from '../../modules/app-sidebar/views/sidebar-layout';
import { WorkbenchService } from '../../modules/workbench';
import { IntegrationGridIcon } from '../integration-grid-icon';
import { NotificationListPopover } from '../notification/popover';
import { WorkspaceNavigator } from '../workspace-selector';
import { shouldShowCustomAppsSidebar } from './custom-apps-visibility';
import { DocumentsSidebarContent } from './documents-sidebar-content';
import { HelpButton } from './help-button';
import {
  areaCreatePlaceholder,
  favoritesOverlayHeader,
  favoritesOverlaySections,
  favoritesOverlaySurface,
  liveWorkflowMenuItem,
  mailObjectBadgeIcon,
  mailObjectBaseIcon,
  mailObjectIcon,
  navigationOverlayTitle,
  navigationOverlayTitleIcon,
  navigationOverlayTitleText,
  organizeSections,
  sidebarMainSections,
  sidebarMiddleScroll,
  sidebarMiddleSection,
  sidebarMiddleSectionContent,
  sidebarMobileActiveSectionGroup,
  sidebarOrganization,
  workspaceAreaSection,
  workspaceAreaSeparatorLine,
  workspaceAreaSeparatorRow,
  workspaceHeaderSummary,
  workspaceHeaderSummaryBadge,
  workspaceHeaderSummaryIcon,
  workspaceHeaderSummaryItem,
  workspaceSharedAreaGroup,
} from './index.css';
import { InviteMembersButton } from './invite-members-button';
import { AppSidebarJournalButton } from './journal-button';
import { useLeftSidebarController } from './left-sidebar-controller';
import { createRootAppSidebarResolvedUiStateStorageKey } from './left-sidebar-ui-state';
import { NavigationOverlayPane } from './navigation-overlay-pane';
import {
  NavigationRailGroup,
  NavigationRailLinks,
} from './navigation-rail-group';
import {
  MOBILE_NAVIGATION_RAIL_QUERY,
  TABLET_NAVIGATION_RAIL_QUERY,
  useNavigationRailViewport,
} from './navigation-rail-viewport';
import { NotificationButton } from './notification-button';
import * as notificationButtonStyles from './notification-button.style.css';
import { SidebarAudioPlayer } from './sidebar-audio-player';
import { TrashButton } from './trash-button';
import * as railStyles from './ui-redesign-rail.css';
import { useSidebarAreaHeaderDropTarget } from './use-sidebar-area-header-drop-target';
// TutorialsButton moved to Settings → Tutorials tab
import UserInfo from './user-info';

const markSidebarPerformanceSignal = (signal: string) => {
  if (
    document.documentElement.dataset.streamlainCanvasPerformanceRecording !==
    'true'
  ) {
    return;
  }
  performance.mark(`streamlain:sidebar:${signal}`);
};

const LazyUpdaterButton = BUILD_CONFIG.isElectron
  ? lazy(() =>
      import('./updater-button').then(m => ({ default: m.UpdaterButton }))
    )
  : null;

const STREAMLAIN_SIDEBAR_ICON_STROKE_WIDTH = 1.25;
const SIDEBAR_BADGE_MAX_COUNT = 99999;
const WORKSPACE_HEADER_NOTIFICATION_BADGE_CAPS = [
  SIDEBAR_BADGE_MAX_COUNT,
  99,
  9,
];
const WORKSPACE_HEADER_MAIL_BADGE_CAPS = [99];
const WORKSPACE_HEADER_SUMMARY_ITEM_GAP = 8;
const WORKSPACE_HEADER_SUMMARY_ICON_BADGE_GAP = 4;
const WORKSPACE_HEADER_SUMMARY_BADGE_HORIZONTAL_PADDING = 8;
const WORKSPACE_HEADER_SUMMARY_BADGE_CHARACTER_WIDTH = 7;
const APP_AREA_ROOT_SYSTEM_KIND = 'app_area_root';
const CUSTOM_APP_FOLDER_SYSTEM_KIND = 'custom_app_folder';
const TEAMSPACE_ROOT_SYSTEM_KIND = 'teamspace_root';
const TEAM_MAILBOXES_FOLDER_SYSTEM_KIND = 'team_mailboxes_folder';
const streamlainSidebarIconProps = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: STREAMLAIN_SIDEBAR_ICON_STROKE_WIDTH,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} satisfies SVGProps<SVGSVGElement>;

const findSidebarDocElement = (
  root: HTMLElement,
  docId: string
): HTMLElement | null =>
  Array.from(
    root.querySelectorAll<HTMLElement>('[data-testid^="navigation-panel-doc-"]')
  ).find(
    element => element.dataset.testid === `navigation-panel-doc-${docId}`
  ) ?? null;

const findSidebarFolderElement = (
  root: HTMLElement,
  folderId: string
): HTMLElement | null =>
  Array.from(
    root.querySelectorAll<HTMLElement>(
      '[data-testid^="navigation-panel-folder-"], [data-teamspace-root-id]'
    )
  ).find(
    element =>
      element.dataset.testid === `navigation-panel-folder-${folderId}` ||
      element.dataset.teamspaceRootId === folderId
  ) ?? null;

const isSidebarTargetRootSectionExpanded = (element: HTMLElement) =>
  element.closest<HTMLElement>(
    '[data-testid="sidebar-personal"], [data-testid="sidebar-team-areas"]'
  )?.dataset.expanded !== 'false';

const scrollSidebarElementIntoView = (element: HTMLElement) => {
  const viewport = element.closest<HTMLElement>(
    '[data-sidebar-scroll-viewport="true"]'
  );
  if (!viewport) {
    element.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();
  const nextScrollTop = viewport.scrollTop + elementRect.top - viewportRect.top;
  const maxScrollTop = Math.max(
    0,
    viewport.scrollHeight - viewport.clientHeight
  );

  viewport.scrollTo({
    top: Math.min(maxScrollTop, Math.max(0, nextScrollTop)),
    behavior: 'smooth',
  });
};

function getSidebarFolderPath(
  folder: AccessibleFolderMeta,
  foldersById: Map<string, AccessibleFolderMeta>,
  workspaceRootFolderId: string | null,
  visited = new Set<string>()
): string[] | null {
  if (visited.has(folder.id)) {
    return null;
  }
  visited.add(folder.id);

  if (folder.isWorkspaceRoot || folder.id === workspaceRootFolderId) {
    return ['organize'];
  }
  if (folder.systemKind === 'private_root') {
    return ['private'];
  }
  if (folder.systemKind === APP_AREA_ROOT_SYSTEM_KIND) {
    return ['app-area'];
  }
  if (folder.systemKind === TEAMSPACE_ROOT_SYSTEM_KIND) {
    return ['teamspace', folder.id];
  }

  const parent = folder.parentId ? foldersById.get(folder.parentId) : null;
  const parentPath = parent
    ? getSidebarFolderPath(parent, foldersById, workspaceRootFolderId, visited)
    : folder.parentId === null
      ? ['organize']
      : null;
  return parentPath ? [...parentPath, `folder-${folder.id}`] : null;
}

function getSidebarFolderPathForDoc(
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string
): string[] | null {
  const folders = accessibleFolderCache.getCachedFolders();
  const foldersById = new Map(folders.map(folder => [folder.id, folder]));
  const workspaceRootFolderId =
    accessibleFolderCache.getCachedWorkspaceRootFolderId();

  if (
    workspaceRootFolderId &&
    accessibleFolderCache.getCachedRootFolderDocIds().includes(docId)
  ) {
    const rootFolder = foldersById.get(workspaceRootFolderId);
    return rootFolder
      ? getSidebarFolderPath(rootFolder, foldersById, workspaceRootFolderId)
      : ['organize'];
  }

  const containingFolder = folders.find(folder =>
    (folder.docIds ?? []).includes(docId)
  );
  return containingFolder
    ? getSidebarFolderPath(containingFolder, foldersById, workspaceRootFolderId)
    : null;
}

function getSidebarFolderPathForFolder(
  accessibleFolderCache: AccessibleFolderCacheStore,
  folderId: string
): string[] | null {
  const folders = accessibleFolderCache.getCachedFolders();
  const foldersById = new Map(folders.map(folder => [folder.id, folder]));
  const folder = foldersById.get(folderId);
  return folder
    ? getSidebarFolderPath(
        folder,
        foldersById,
        accessibleFolderCache.getCachedWorkspaceRootFolderId()
      )
    : null;
}

function useMaterializeFolderChildren(
  accessibleFolderCache: AccessibleFolderCacheStore,
  folderIds: readonly string[],
  diagnosticReason: string
) {
  useEffect(() => {
    for (const folderId of folderIds) {
      accessibleFolderCache
        .fetchFolderChildren(folderId, { diagnosticReason })
        .catch(() => {});
    }
  }, [accessibleFolderCache, diagnosticReason, folderIds]);
}

function expandSidebarFolderPath(
  navigationPanelService: NavigationPanelService,
  path: string[]
) {
  const startIndex = path[0] === 'teamspace' ? 2 : 1;
  for (let index = startIndex; index <= path.length; index++) {
    navigationPanelService.setCollapsed(path.slice(0, index), false);
  }
}

function getRootSidebarSectionForFolderPath(
  path: string[]
): 'personal' | 'teamAreas' | 'apps' | null {
  if (path[0] === 'private') return 'personal';
  if (path[0] === 'organize' || path[0] === 'teamspace') return 'teamAreas';
  if (path[0] === 'app-area') return 'apps';
  return null;
}

export type RootAppSidebarProps = {
  isPublicWorkspace: boolean;
  onOpenQuickSearchModal: () => void;
  onOpenSettingModal: () => void;
  currentWorkspace: Workspace;
  openPage: (pageId: string) => void;
  createPage: () => Store;
  paths: {
    all: (workspaceId: string) => string;
    trash: (workspaceId: string) => string;
    shared: (workspaceId: string) => string;
  };
};

type WorkspaceAreaCreationContextValue = {
  areas: WorkspaceAreaRef[];
  canCreateArea: boolean;
  createArea: () => Promise<void>;
  newAreaRootFolderId: string | null;
  clearNewAreaRootFolderId: () => void;
  refreshAreas: () => void;
};

type WorkspaceNavigationButtonProps = {
  postfix?: ReactElement;
};

const WorkspaceAreaCreationContext =
  createContext<WorkspaceAreaCreationContextValue | null>(null);

const useWorkspaceAreaCreation = () => useContext(WorkspaceAreaCreationContext);
const noopRefreshWorkspaceAreas = () => {};
const noopClearNewWorkspaceArea = () => {};

const enqueueFolderCreatePermissionGrants = (
  objectPermissionMutation: ObjectPermissionMutationService,
  folderId: string,
  config: FolderCreateConfig
) =>
  objectPermissionMutation.grantPendingAssignments(
    { id: folderId, kind: 'folder' },
    config
  );

const AllDocsButton = ({ postfix }: WorkspaceNavigationButtonProps) => {
  const t = useI18n();
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/all')
  );

  return (
    <MenuLinkItem
      icon={<AllDocsIcon />}
      active={allPageActive}
      to={'/all'}
      postfix={postfix}
    >
      <span data-testid="all-pages">
        {t['com.streamlain.workspaceSubPath.all']()}
      </span>
    </MenuLinkItem>
  );
};

const PlannerButton = ({ postfix }: WorkspaceNavigationButtonProps) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const { isFeatureAllowed } = useFeatureGate();
  const plannerActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/planner')
  );

  if (
    workspace.flavour !== 'local' &&
    !isFeatureAllowed(FEATURE_KEY_DATABASE_CALENDAR)
  ) {
    return null;
  }

  return (
    <MenuLinkItem
      icon={<TodayIcon />}
      active={plannerActive}
      to="/planner"
      postfix={postfix}
      onClick={() => {
        if (plannerActive) {
          requestNativeWorkspaceAppSidebar('planner', 'open');
        }
      }}
    >
      <span data-testid="sidebar-calendar">
        {t['com.streamlain.rootAppSidebar.planner']()}
      </span>
    </MenuLinkItem>
  );
};

/**
 * sl-bpu6.1.2: Custom Apps sidebar entry.
 * sl-xm1c.2.5: Visibility now combines plan-gate AND workspace-admin toggle —
 * Free plans hide the link entirely (matches the rest of the custom-apps
 * surface), and admins who switched the feature off in workspace settings
 * also drop it. role >= Member is implicit for the sidebar.
 */
const useCanShowCustomAppsSidebarButton = () => {
  const workspace = useService(WorkspaceService).workspace;
  const { isFeatureAllowed } = useFeatureGate();
  const hasCustomApps = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);
  const canViewCustomApps = useGuard('Workspace_CustomApps_View') ?? false;
  const shareSetting = useService(WorkspaceShareSettingService).sharePreview;
  const customAppsEnabled = useLiveData(shareSetting.customAppsEnabled$);
  const { apps } = useWorkspaceLaunchableCustomApps(
    workspace.flavour === 'local' || customAppsEnabled !== true
      ? null
      : workspace.id
  );
  const hasLaunchableApps = (apps ?? []).some(app => app.launchable);
  const canManageApps =
    hasCustomApps && canViewCustomApps && customAppsEnabled === true;

  // sl-xm1c.2.5: While the workspace toggle is still loading, hide the entry.
  // Treat null (loading) and false (admin-disabled) the same. The plan
  // entitlement remains authoritative even if SWR still holds launch data
  // from before a downgrade.
  return shouldShowCustomAppsSidebar({
    hasEntitlement: hasCustomApps,
    workspaceEnabled: customAppsEnabled === true,
    canManageApps,
    hasLaunchableApps,
  });
};

const CustomAppsButton = (props: WorkspaceNavigationButtonProps) => {
  const canShowCustomApps = useCanShowCustomAppsSidebarButton();
  return canShowCustomApps ? <CustomAppsButtonLink {...props} /> : null;
};

const CustomAppsButtonLink = ({ postfix }: WorkspaceNavigationButtonProps) => {
  const t = useI18n();
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const customAppsPathname = useLiveData(
    workbench.location$.selector(location => location.pathname)
  );
  const customAppsActive =
    customAppsPathname === '/custom-apps' ||
    customAppsPathname.startsWith('/custom-apps/');
  const appsLabel = t['com.streamlain.customApps.title']();

  return (
    <MenuLinkItem
      icon={<IntegrationGridIcon topRight="filled" />}
      active={customAppsActive}
      to="/custom-apps"
      postfix={postfix}
    >
      <span data-testid="sidebar-custom-apps">{appsLabel}</span>
    </MenuLinkItem>
  );
};

/**
 * sl-sdk6.6.3: Graph Explorer sidebar entry — "Firmenwissen"
 * Visible for Pro+ plans when AI and knowledge graph features are available.
 */
const useCanShowGraphExplorerSidebarButton = () => {
  const featureFlagService = useService(FeatureFlagService);
  const serverService = useService(ServerService);
  const serverFeatures = useLiveData(serverService.server.features$);
  const enableAI = useLiveData(featureFlagService.flags.enable_ai.$);
  const { isFeatureAllowed } = useFeatureGate();
  const canUseKnowledgeGraph = useGuard('Workspace_KnowledgeGraph');

  return (
    canUseKnowledgeGraph === true &&
    !!enableAI &&
    !!serverFeatures?.copilot &&
    isFeatureAllowed('managed-ai-credits') &&
    isFeatureAllowed('ai-knowledge-graph')
  );
};

const GraphExplorerButton = (props: WorkspaceNavigationButtonProps) => {
  const canShowGraphExplorer = useCanShowGraphExplorerSidebarButton();
  return canShowGraphExplorer ? <GraphExplorerButtonLink {...props} /> : null;
};

const GraphExplorerButtonLink = ({
  postfix,
}: WorkspaceNavigationButtonProps) => {
  const t = useI18n();
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const graphExplorerActive = useLiveData(
    workbench.location$.selector(
      location => location.pathname === '/graph-explorer'
    )
  );

  return (
    <MenuLinkItem
      icon={
        <svg {...streamlainSidebarIconProps}>
          <path d="M4 6h4" />
          <path d="M4 12h4" />
          <path d="M4 18h4" />
          <path d="M8 6v12" />
          <rect x="10" y="4" width="10" height="4" rx="2" />
          <rect x="10" y="10" width="10" height="4" rx="2" />
          <rect x="10" y="16" width="10" height="4" rx="2" />
        </svg>
      }
      active={graphExplorerActive}
      to={'/graph-explorer'}
      postfix={postfix}
    >
      <span data-testid="graph-explorer">
        {t['com.streamlain.graph-explorer.title']()}
      </span>
    </MenuLinkItem>
  );
};

/**
 * sl-u319.2.1: Marketplace sidebar entry — feature-gated via marketplace-browse.
 */
const MarketplaceButton = ({
  label,
  postfix,
  railTabs = false,
}: WorkspaceNavigationButtonProps & {
  label?: string;
  railTabs?: boolean;
}) => {
  const t = useI18n();
  const { isFeatureAllowed } = useFeatureGate();
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const docSwitcher = useService(DocSwitcherService);
  const recentDestinations = useLiveData(docSwitcher.availableDestinations$);
  const marketplaceLocation = useLiveData(
    workbench.location$.selector(
      location => `${location.pathname}${location.search}`
    )
  );
  const [marketplacePathname, marketplaceSearch = ''] =
    marketplaceLocation.split('?');
  const marketplaceActive = marketplacePathname === '/marketplace';
  const marketplaceTab = marketplaceActive
    ? (new URLSearchParams(marketplaceSearch).get('tab') ?? 'marketplace')
    : null;
  const marketplaceResumeTo =
    recentDestinations.find(destination => destination.kind === 'hub')?.to ??
    '/marketplace';

  if (!isFeatureAllowed('marketplace-browse')) {
    return null;
  }

  const primary = (
    <MenuLinkItem
      icon={<IntegrationGridIcon topRight="plus" />}
      active={marketplaceActive}
      to={marketplaceResumeTo}
      postfix={postfix}
    >
      <span data-testid="sidebar-marketplace">
        {label ?? t['com.streamlain.marketplace.page.title']()}
      </span>
    </MenuLinkItem>
  );

  if (!railTabs) return primary;

  return (
    <NavigationRailGroup
      active={marketplaceActive}
      ariaLabel={t['com.streamlain.marketplace.page.title']()}
      primary={primary}
      testId="global-navigation-marketplace-tabs"
    >
      <NavigationRailLinks
        items={[
          {
            active: marketplaceTab === 'marketplace',
            icon: <PublishIcon />,
            label: t['com.streamlain.marketplace.rail.market'](),
            to: '/marketplace',
          },
          {
            active: marketplaceTab === 'workspace',
            icon: <TeamWorkspaceIcon />,
            label: t['com.streamlain.marketplace.page.tab.workspace'](),
            to: '/marketplace?tab=workspace',
          },
          {
            active: marketplaceTab === 'published',
            icon: <AccountIcon />,
            label: t['com.streamlain.marketplace.rail.mine'](),
            to: '/marketplace?tab=published',
          },
        ]}
      />
    </NavigationRailGroup>
  );
};

/**
 * Workflows sidebar entry — navigates to /workflows page with standalone Workflow Builder.
 * Gated via the 'streamlain-workflows' feature key so Free (and local) users don't see it.
 */
const WorkflowsButton = ({ postfix }: WorkspaceNavigationButtonProps) => {
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const { isFeatureAllowed } = useFeatureGate();
  const workflowsActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/workflows')
  );
  const canShowWorkflows = isFeatureAllowed(FEATURE_KEY_WORKFLOW);

  if (!canShowWorkflows) {
    return null;
  }

  return (
    <WorkflowsButtonLink workflowsActive={workflowsActive} postfix={postfix} />
  );
};

const WorkflowsButtonLink = ({
  postfix,
  workflowsActive,
}: {
  postfix?: ReactElement;
  workflowsActive: boolean;
}) => {
  const t = useI18n();
  const workflowService = useServiceOptional(WorkflowService);
  const workflowPresence = useServiceOptional(WorkflowLivePresenceService);
  const workflows = useLiveData(workflowService?.workflows$ ?? null);
  const participantsByWorkflow = useLiveData(
    workflowPresence?.participantsByWorkflow$ ?? null
  );
  const workflowIdsKey = useMemo(
    () => workflows?.map(workflow => workflow.id).join('\u001f') ?? '',
    [workflows]
  );
  const hasWorkflowPresence =
    !!participantsByWorkflow &&
    !!workflowIdsKey &&
    workflowIdsKey
      .split('\u001f')
      .some(
        workflowId => (participantsByWorkflow.get(workflowId)?.length ?? 0) > 0
      );

  useEffect(() => {
    if (!workflowPresence || !workflowIdsKey) return;
    const workflowIds = workflowIdsKey.split('\u001f');
    void workflowPresence.watchWorkflowIds(workflowIds);
    return () => workflowPresence.unwatchWorkflowIds(workflowIds);
  }, [workflowIdsKey, workflowPresence]);

  return (
    <MenuLinkItem
      className={hasWorkflowPresence ? liveWorkflowMenuItem : undefined}
      icon={<IntegrationGridIcon topRight="play" />}
      active={workflowsActive}
      to="/workflows"
      postfix={postfix}
    >
      <span data-testid="sidebar-workflows">
        {t['com.streamlain.streamlain.workflow.page.title']()}
      </span>
    </MenuLinkItem>
  );
};

const WorkspaceMailButton = () => {
  const workspace = useService(WorkspaceService).workspace;
  const { isFeatureAllowed } = useFeatureGate();

  if (
    workspace.flavour === 'local' ||
    getWorkspaceMailMissingFeature(isFeatureAllowed) !== null
  ) {
    return null;
  }

  return <MailInboxButton workspaceId={workspace.id} />;
};

const PrivateFolderIcon = ({ className }: { className?: string }) => (
  <FolderIcon className={className} />
);

const MailFolderIcon = ({ className }: { className?: string }) => (
  <span className={clsx(mailObjectIcon, className)}>
    <FolderIcon className={mailObjectBaseIcon} />
    <EmailIcon className={mailObjectBadgeIcon} />
  </span>
);

const MailDocIcon = ({ className }: { className?: string }) => (
  <span className={clsx(mailObjectIcon, className)}>
    <PageIcon className={mailObjectBaseIcon} />
    <EmailIcon className={mailObjectBadgeIcon} />
  </span>
);

type PrivateDropPosition =
  | { entityType: 'doc'; at: 'before' | 'after'; entityId: string }
  | { entityType: 'folder'; at: 'before' | 'after'; entityId: string };

type PrivateSidebarChildItem =
  | { key: string; label: string; type: 'doc'; docId: string }
  | {
      key: string;
      label: string;
      type: 'folder';
      folder: AccessibleFolderMeta;
    };

type AppAreaSidebarChildItem =
  | { key: string; label: string; type: 'doc'; docId: string }
  | {
      key: string;
      label: string;
      type: 'folder';
      folder: AccessibleFolderMeta;
    };

type WorkspaceSidebarAreaSectionItem = {
  key: string;
  label: string;
  render: (reorderActions?: SidebarAreaReorderActions) => ReactElement | null;
};

function compareWorkspaceSidebarAreaSections(
  a: WorkspaceSidebarAreaSectionItem,
  b: WorkspaceSidebarAreaSectionItem
): number {
  return compareSidebarManualOrderItems(a, b);
}

const privateFolderDropEffect: NavigationPanelTreeNodeDropEffect = data => {
  const entityType = data.source.data.entity?.type;
  if (entityType !== 'folder') {
    return;
  }
  if (
    data.treeInstruction?.type === 'make-child' ||
    data.treeInstruction?.type === 'reorder-above' ||
    data.treeInstruction?.type === 'reorder-below'
  ) {
    return 'move';
  }
  return;
};

function getPrivateDropPosition(
  data: DropTargetDropEvent<AffineDNDData>,
  entityType: 'doc' | 'folder',
  entityId: string
): PrivateDropPosition | undefined {
  if (
    data.treeInstruction?.type !== 'reorder-above' &&
    data.treeInstruction?.type !== 'reorder-below'
  ) {
    return;
  }

  return {
    entityType,
    entityId,
    at: data.treeInstruction.type === 'reorder-below' ? 'after' : 'before',
  };
}

function toSidebarOrderPosition(
  position: PrivateDropPosition
): SidebarManualOrderPosition {
  return {
    at: position.at,
    key: sidebarManualOrderKey(position.entityType, position.entityId),
  };
}

function canDropPrivateEntity(
  args: DropTargetGetFeedback<AffineDNDData>,
  options: {
    targetFolderId?: string;
    allFolders?: AccessibleFolderMeta[];
    allowMakeChild?: boolean;
    canDropDoc?: (args: DropTargetGetFeedback<AffineDNDData>) => boolean;
  } = {}
): boolean {
  const entity = args.source.data.entity;
  const instructionType = args.treeInstruction?.type;
  if (
    instructionType &&
    instructionType !== 'make-child' &&
    instructionType !== 'reorder-above' &&
    instructionType !== 'reorder-below'
  ) {
    return false;
  }
  if (instructionType === 'make-child' && options.allowMakeChild === false) {
    return false;
  }

  if (entity?.type === 'doc') {
    return options.canDropDoc?.(args) ?? false;
  }
  if (entity?.type !== 'folder') {
    return false;
  }

  if (!options.targetFolderId) {
    return true;
  }
  if (entity.id === options.targetFolderId) {
    return false;
  }

  return !isFolderDescendant(
    options.targetFolderId,
    entity.id,
    options.allFolders ?? []
  );
}

function getCachedDocSourceFolderId(
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string
): string | null {
  const cachedFolder = accessibleFolderCache
    .getCachedFolders()
    .find(folder => folder.docIds.includes(docId));
  if (cachedFolder) {
    return cachedFolder.id;
  }

  const rootFolderId = accessibleFolderCache.getCachedWorkspaceRootFolderId();
  if (
    rootFolderId &&
    accessibleFolderCache.getCachedRootFolderDocIds().includes(docId)
  ) {
    return rootFolderId;
  }

  return null;
}

function cachedFolderAllowsDocRemoval(
  workspaceId: string,
  accessibleFolderCache: AccessibleFolderCacheStore,
  docId: string,
  targetFolderId: string
): boolean {
  const sourceFolderId = getCachedDocSourceFolderId(
    accessibleFolderCache,
    docId
  );
  if (!sourceFolderId || sourceFolderId === targetFolderId) {
    return true;
  }

  const sourceFolder = accessibleFolderCache.getCachedFolder(sourceFolderId);
  if (sourceFolder?.systemKind === 'private_mail') {
    return false;
  }

  const sourcePermissions = getRememberedFolderPermissions(
    workspaceId,
    sourceFolderId
  );
  return sourcePermissions?.Folder_RemoveDoc !== false;
}

function cachedFolderAllowsDocAdd(
  workspaceId: string,
  targetFolderId: string,
  targetPermissions?: FolderPermissions | null
): boolean {
  const permissions =
    targetPermissions ??
    getRememberedFolderPermissions(workspaceId, targetFolderId);
  return permissions?.Folder_AddDoc !== false;
}

function createOptimisticPrivateFolderMeta({
  id,
  inheritPermissions = true,
  name,
  parentId,
  ownerUserId = null,
  visibility = 'private',
}: {
  id: string;
  inheritPermissions?: boolean;
  name: string;
  parentId: string;
  ownerUserId?: string | null;
  visibility?: 'public' | 'private';
}): AccessibleFolderMeta {
  return {
    id,
    name,
    parentId,
    ownerUserId,
    systemKind: null,
    visibility,
    inheritPermissions,
    isWorkspaceRoot: false,
    immutable: false,
    storageLimitBytes: null,
    storageUsedBytes: null,
    metadataOnly: false,
    docIds: [],
  };
}

const TeamAreaAddPageButton = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const { canCreateDocEffective } = useWorkspaceCreateDocPermission();
  const connectivityStatus = useConnectivityStatus();
  const workspaceAreaCreation = useWorkspaceAreaCreation();
  const dynamicDocTargets = useMemo(
    () =>
      (workspaceAreaCreation?.areas ?? [])
        .filter(
          area => area.kind === 'teamspace' && area.permissions.Folder_AddDoc
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(area => ({
          folderId: area.rootFolderId,
          label: area.name,
        })),
    [workspaceAreaCreation?.areas]
  );
  const handleCreateTeamspaceMenuClick = useCallback(() => {
    if (!connectivityStatus.backendAvailable) {
      notify.error({
        title: t['com.streamlain.settings.connection-required.title'](),
        message: t['com.streamlain.settings.connection-required.description'](),
      });
      return;
    }
    void workspaceAreaCreation?.createArea();
  }, [connectivityStatus.backendAvailable, t, workspaceAreaCreation]);
  const createTeamspaceMenuItem = workspaceAreaCreation?.canCreateArea ? (
    <ComponentMenuItem
      data-testid="sidebar-new-teamspace-button"
      prefixIcon={<AddOrganizeIcon />}
      onClick={handleCreateTeamspaceMenuClick}
    >
      {t['com.streamlain.rootAppSidebar.teamspace.create']()}
    </ComponentMenuItem>
  ) : null;
  const createTooltip =
    createTeamspaceMenuItem || dynamicDocTargets.length > 0
      ? t['Create']()
      : undefined;
  if (workspace.flavour === 'local') {
    return (
      <AddPageButton
        createItems={createTeamspaceMenuItem}
        dynamicTargets={dynamicDocTargets}
        tooltip={createTooltip}
      />
    );
  }

  if (!canCreateDocEffective) {
    return null;
  }

  return (
    <FolderTargetAddPageButton
      createItems={createTeamspaceMenuItem}
      dynamicTargets={dynamicDocTargets}
      tooltip={createTooltip}
    />
  );
};

const PersonalAddPageButton = () => {
  const workspace = useService(WorkspaceService).workspace;
  const account = useLiveData(useService(AuthService).session.account$);
  const { canCreateDocEffective } = useWorkspaceCreateDocPermission();
  const { isFeatureAllowed } = useFeatureGate();

  if (workspace.flavour === 'local') {
    return null;
  }

  if (
    !canCreateDocEffective ||
    !account?.id ||
    !isFeatureAllowed(FEATURE_KEY_PRIVATE_FOLDERS)
  ) {
    return null;
  }

  return (
    <FolderTargetAddPageButton
      includePrivateFolder
      privateFolderId={`private_${workspace.id}_${account.id}`}
      showPublicItems={false}
    />
  );
};

const FolderTargetAddPageButton = ({
  createItems,
  dynamicTargets,
  includePrivateFolder = false,
  privateFolderId,
  showPublicItems = true,
  tooltip,
}: {
  createItems?: ReactNode;
  dynamicTargets?: { folderId: string; label: string }[];
  includePrivateFolder?: boolean;
  privateFolderId?: string | null;
  showPublicItems?: boolean;
  tooltip?: string;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const privateRoot = useMemo(
    () =>
      accessibleFolders.find(folder => folder.systemKind === 'private_root') ??
      null,
    [accessibleFolders]
  );
  const refreshPrivateFolders = useCallback(() => {
    const refresh = () => accessibleFolderCache.fetchAccessibleFolders();
    refresh().catch(() => {});
    setTimeout(() => {
      refresh().catch(() => {});
    }, 750);
    setTimeout(() => {
      refresh().catch(() => {});
    }, 2000);
  }, [accessibleFolderCache]);
  const handleFolderDocCreated = useCallback(
    (folderId: string, docId: string) => {
      if (privateRoot?.id === folderId) {
        accessibleFolderCache.optimisticAddDocToFolder(privateRoot.id, docId);
      } else {
        accessibleFolderCache.optimisticAddDocToFolder(folderId, docId);
      }
      refreshPrivateFolders();
    },
    [accessibleFolderCache, privateRoot?.id, refreshPrivateFolders]
  );
  const handleDocCreated = useCallback(
    (docId: string) => {
      navigationPanelRevealService.revealDoc(docId, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId: workspace.id,
      });
    },
    [navigationPanelRevealService, workspace.id]
  );
  const resolvedPrivateFolderId =
    privateFolderId ?? (includePrivateFolder ? privateRoot?.id : null);

  if (includePrivateFolder && !resolvedPrivateFolderId) {
    return null;
  }

  return (
    <AddPageButton
      createItems={createItems}
      dynamicTargets={dynamicTargets}
      onDocCreated={handleDocCreated}
      onFolderDocCreated={handleFolderDocCreated}
      privateFolderId={resolvedPrivateFolderId}
      showPublicItems={showPublicItems}
      tooltip={tooltip}
    />
  );
};

const PrivateSidebarSection = ({
  hideJournalFolder = false,
  revealOnExpand = false,
}: {
  hideJournalFolder?: boolean;
  revealOnExpand?: boolean;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const { isFeatureAllowed } = useFeatureGate();
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();

  if (workspace.flavour === 'local') {
    return null;
  }
  const hasPrivateFolders = isFeatureAllowed(FEATURE_KEY_PRIVATE_FOLDERS);
  if (!hasPrivateFolders) {
    return null;
  }

  return (
    <PrivateSidebarSectionContent
      hideJournalFolder={hideJournalFolder}
      revealOnExpand={revealOnExpand}
      showMailFolder={getWorkspaceMailMissingFeature(isFeatureAllowed) === null}
      hiddenMailFolderDocIds={hiddenMailFolderDocIds}
    />
  );
};

const PrivateSidebarSectionContent = ({
  hideJournalFolder,
  revealOnExpand,
  showMailFolder,
  hiddenMailFolderDocIds,
}: {
  hideJournalFolder: boolean;
  revealOnExpand: boolean;
  showMailFolder: boolean;
  hiddenMailFolderDocIds: ReadonlySet<string>;
}) => {
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;
  const navigationPanelService = useService(NavigationPanelService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const organizeService = useService(OrganizeService);
  const authService = useService(AuthService);
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const t = useI18n();
  const account = useLiveData(authService.session.account$);
  const workspaceAreaCreation = useWorkspaceAreaCreation();
  const { canCreateDocEffective } = useWorkspaceCreateDocPermission();
  const pageHelper = usePageHelper(workspace.docCollection);
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const privatePath = useMemo(() => ['private'], []);
  const privateCollapsed = useLiveData(
    navigationPanelService.collapsed$(privatePath)
  );

  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const privateRoot = useMemo(
    () =>
      accessibleFolders.find(folder => folder.systemKind === 'private_root') ??
      null,
    [accessibleFolders]
  );
  const privateRootId = privateRoot?.id ?? null;
  const privateArea = useMemo(
    () =>
      (workspaceAreaCreation?.areas ?? []).find(
        area => area.kind === 'private' && area.rootFolderId === privateRootId
      ) ?? null,
    [privateRootId, workspaceAreaCreation?.areas]
  );
  const canCreatePrivateDocument =
    canCreateDocEffective && privateArea?.permissions.Folder_AddDoc === true;
  const canCreatePrivateFolder =
    privateArea?.permissions.Folder_CreateSubfolder === true;
  const privateRootDocIds = useMemo(
    () => privateRoot?.docIds ?? [],
    [privateRoot?.docIds]
  );
  const mailFolder = privateRootId
    ? (accessibleFolders.find(
        folder =>
          folder.parentId === privateRootId &&
          folder.systemKind === 'private_mail'
      ) ?? null)
    : null;
  const mailFolderIds = useMemo(
    () => (mailFolder ? [mailFolder.id] : []),
    [mailFolder]
  );
  useMaterializeFolderChildren(
    accessibleFolderCache,
    mailFolderIds,
    'root-sidebar:private-mail-folder'
  );
  const visibleMailDocIds = useMemo(
    () =>
      (mailFolder?.docIds ?? []).filter(
        docId => !hiddenMailFolderDocIds.has(docId)
      ),
    [hiddenMailFolderDocIds, mailFolder?.docIds]
  );
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const docTrashStateById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.trashed])),
    [accessibleDocs]
  );
  const privateRootFolders = useMemo(
    () =>
      privateRootId
        ? accessibleFolders.filter(
            folder =>
              folder.parentId === privateRootId &&
              folder.systemKind !== 'private_mail' &&
              (!hideJournalFolder ||
                folder.id !== journalFolderId(privateRootId))
          )
        : [],
    [accessibleFolders, hideJournalFolder, privateRootId]
  );

  useEffect(() => {
    if (!privateRootId) return;
    accessibleFolderCache.fetchFolderChildren(privateRootId).catch(() => {});
  }, [accessibleFolderCache, privateRootId]);

  const privateRootItems = useMemo<PrivateSidebarChildItem[]>(
    () => [
      ...privateRootDocIds
        .filter(docId => docTrashStateById.get(docId) !== true)
        .map(docId => ({
          key: sidebarManualOrderKey('doc', docId),
          label: docTitleById.get(docId) ?? '',
          type: 'doc' as const,
          docId,
        })),
      ...privateRootFolders.map(folder => ({
        key: sidebarManualOrderKey('folder', folder.id),
        label: folder.name,
        type: 'folder' as const,
        folder,
      })),
    ],
    [docTitleById, docTrashStateById, privateRootDocIds, privateRootFolders]
  );
  const privateRootOrderStorageKey = privateRoot
    ? `streamlain:private-sidebar-manual-order:${workspace.id}:${privateRoot.id}`
    : null;
  const {
    orderedItems: orderedPrivateRootItems,
    placeItem: placePrivateRootItem,
    setSortMode: setPrivateRootSortMode,
    sortMode: privateRootSortMode,
  } = useSidebarManualOrder(
    privateRootOrderStorageKey,
    privateRootItems,
    compareSidebarManualOrderItems
  );

  const handleCreatePrivateDoc = useCallback(
    (config: DocCreateConfig) => {
      if (!privateRoot || !canCreatePrivateDocument) return;
      const newDoc = pageHelper.createPage(undefined, {
        show: false,
        targetFolderId: privateRoot.id,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(privateRoot.id, newDoc.id);
      if (privateRootSortMode === 'manual') {
        placePrivateRootItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      void objectPermissionMutation
        .grantPendingAssignments({ id: newDoc.id, kind: 'doc' }, config)
        .catch(error => {
          console.error('Failed to apply private document permissions:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
      navigationPanelService.setCollapsed(['private'], false);
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId,
      });
      void accessibleFolderCache.fetchAccessibleFolders();
    },
    [
      accessibleFolderCache,
      canCreatePrivateDocument,
      navigationPanelRevealService,
      navigationPanelService,
      objectPermissionMutation,
      pageHelper,
      placePrivateRootItem,
      privateRoot,
      privateRootSortMode,
      t,
      workspaceId,
    ]
  );

  const handleCreatePrivateFolder = useCallback(
    (config: FolderCreateConfig) => {
      if (!privateRoot || !canCreatePrivateFolder) return;
      const folderId = nanoid();
      const snapshot = accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticPrivateFolderMeta({
          id: folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          parentId: privateRoot.id,
          ownerUserId: account?.id ?? null,
          visibility: config.visibility,
        }),
        { preserveUntilServerAck: true }
      );
      if (privateRootSortMode === 'manual') {
        placePrivateRootItem(sidebarManualOrderKey('folder', folderId));
      }
      setNewFolderId(folderId);
      navigationPanelService.setCollapsed(['private'], false);

      void folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          name: config.name,
          parentId: privateRoot.id,
          visibility: config.visibility,
          inheritPermissions: config.inheritPermissions,
        })
        .then(() =>
          enqueueFolderCreatePermissionGrants(
            objectPermissionMutation,
            folderId,
            config
          ).catch(error => {
            console.error('Failed to apply private folder permissions:', error);
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.folder-permissions-failed'
                ](),
            });
          })
        )
        .catch(error => {
          console.error('Failed to create private folder:', error);
          accessibleFolderCache.restoreOptimisticSnapshot(snapshot);
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.createFailed'](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      canCreatePrivateFolder,
      folderMutationOutbox,
      navigationPanelService,
      objectPermissionMutation,
      placePrivateRootItem,
      privateRoot,
      privateRootSortMode,
      t,
    ]
  );

  const handleCreatePrivateDocImmediately = useCallback(() => {
    handleCreatePrivateDoc(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleCreatePrivateDoc, t]);

  const handleCreatePrivateFolderImmediately = useCallback(() => {
    handleCreatePrivateFolder(
      createDefaultFolderCreateConfig(
        'private-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleCreatePrivateFolder, t]);

  const moveEntityToPrivateFolder = useCallback(
    async (
      data: DropTargetDropEvent<AffineDNDData>,
      targetFolderId: string,
      position?: PrivateDropPosition
    ) => {
      const entity = data.source.data.entity;
      if (!entity) {
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();

      if (entity.type === 'doc') {
        if (
          !cachedFolderAllowsDocAdd(workspaceId, targetFolderId) ||
          !cachedFolderAllowsDocRemoval(
            workspaceId,
            accessibleFolderCache,
            entity.id,
            targetFolderId
          )
        ) {
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
              ](),
          });
          return;
        }

        accessibleFolderCache.optimisticAddDocToFolder(
          targetFolderId,
          entity.id,
          position?.entityType === 'doc'
            ? {
                at: position.at,
                docId: position.entityId,
              }
            : undefined
        );
        if (targetFolderId === privateRoot?.id && position) {
          placePrivateRootItem(
            sidebarManualOrderKey('doc', entity.id),
            toSidebarOrderPosition(position)
          );
        }
        navigationPanelService.setCollapsed(['private'], false);

        void folderMutationOutbox
          .enqueueAddDocToFolder(targetFolderId, entity.id)
          .catch(error => {
            console.error('Failed to move doc to private folder:', error);
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
                ](),
            });
          });
        return;
      }

      if (entity.type === 'folder') {
        if (entity.id === targetFolderId) {
          return;
        }
        const existing = accessibleFolderCache.getCachedFolder(entity.id);
        const yjsNode = organizeService.folderTree.folderNode$(entity.id).value;
        const folderName =
          existing?.name ?? yjsNode?.name$.value ?? t['Folder']();
        if (!existing) {
          accessibleFolderCache.optimisticUpsertFolder(
            createOptimisticPrivateFolderMeta({
              id: entity.id,
              name: folderName,
              parentId: targetFolderId,
              ownerUserId: account?.id ?? null,
            })
          );
        }
        accessibleFolderCache.optimisticMoveFolder(
          entity.id,
          targetFolderId,
          {
            inheritPermissions: existing?.inheritPermissions ?? true,
            name: folderName,
            systemKind: null,
            visibility: 'private',
          },
          position?.entityType === 'folder'
            ? {
                at: position.at,
                folderId: position.entityId,
              }
            : undefined
        );
        if (targetFolderId === privateRoot?.id && position) {
          placePrivateRootItem(
            sidebarManualOrderKey('folder', entity.id),
            toSidebarOrderPosition(position)
          );
        }
        navigationPanelService.setCollapsed(['private'], false);

        void folderMutationOutbox
          .enqueueMoveFolder(entity.id, targetFolderId)
          .catch(error => {
            console.error('Failed to move item to private folder:', error);
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            notify.error({
              title:
                t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
            });
          });
      }
    },
    [
      accessibleFolderCache,
      account?.id,
      folderMutationOutbox,
      navigationPanelService,
      organizeService,
      placePrivateRootItem,
      privateRoot,
      t,
      workspaceId,
    ]
  );

  const handlePrivateRootDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (!privateRoot) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          moveEntityToPrivateFolder(itemData, privateRoot.id).catch(error => {
            console.error('Failed to move item to private root:', error);
          });
        });
        selectionContext?.clearSelection();
        return;
      }
      moveEntityToPrivateFolder(data, privateRoot.id).catch(error => {
        console.error('Failed to move item to private root:', error);
      });
    },
    [moveEntityToPrivateFolder, privateRoot, selectionContext]
  );

  const handlePrivateRootChildDrop = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      position: PrivateDropPosition
    ) => {
      if (!privateRoot) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          moveEntityToPrivateFolder(itemData, privateRoot.id, position).catch(
            error => {
              console.error('Failed to move item inside private root:', error);
            }
          );
        });
        selectionContext?.clearSelection();
        return;
      }
      moveEntityToPrivateFolder(data, privateRoot.id, position).catch(error => {
        console.error('Failed to move item inside private root:', error);
      });
    },
    [moveEntityToPrivateFolder, privateRoot, selectionContext]
  );

  const privateRootCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      if (!privateRoot) {
        return false;
      }
      if (
        privateRootSortMode !== 'manual' &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      return canDropPrivateEntity(args, {
        canDropDoc: docArgs => {
          const entity = docArgs.source.data.entity;
          return (
            entity?.type === 'doc' &&
            cachedFolderAllowsDocAdd(workspaceId, privateRoot.id) &&
            cachedFolderAllowsDocRemoval(
              workspaceId,
              accessibleFolderCache,
              entity.id,
              privateRoot.id
            )
          );
        },
      });
    },
    [accessibleFolderCache, privateRoot, privateRootSortMode, workspaceId]
  );
  const canDropOnPrivateArea =
    privateRootSortMode !== 'manual' || orderedPrivateRootItems.length === 0;

  const { dropTargetRef: privateRootDropTargetRef } =
    useSidebarAreaHeaderDropTarget({
      canDrop: privateRootCanDrop,
      collapsed: privateCollapsed,
      onDrop: handlePrivateRootDrop,
      path: privatePath,
    });
  const { dropTargetRef: privateRootEmptyDropTargetRef } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:organize:root' },
        onDrop: handlePrivateRootDrop,
        canDrop: privateRootCanDrop,
        allowExternal: true,
      }),
      [handlePrivateRootDrop, privateRootCanDrop]
    );
  const { dropTargetRef: privateRootContentDropTargetRef } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:organize:root' },
        onDrop: handlePrivateRootDrop,
        canDrop: args =>
          canDropOnPrivateArea &&
          ((typeof privateRootCanDrop === 'function'
            ? privateRootCanDrop(args)
            : privateRootCanDrop) ??
            false),
        allowExternal: true,
      }),
      [canDropOnPrivateArea, handlePrivateRootDrop, privateRootCanDrop]
    );

  return (
    <>
      <CollapsibleSection
        path={privatePath}
        headerRef={privateRootDropTargetRef}
        testId="sidebar-private"
        title={t['com.streamlain.rootAppSidebar.privateFolder']()}
        revealOnExpand={revealOnExpand}
        actions={
          privateRoot ? (
            <SidebarAreaHeaderActions
              create={{
                actions: buildSidebarCreateActions({
                  document: canCreatePrivateDocument
                    ? {
                        label:
                          t[
                            'com.streamlain.rootAppSidebar.organize.create.document'
                          ](),
                        onSelect: handleCreatePrivateDocImmediately,
                      }
                    : undefined,
                  folder: canCreatePrivateFolder
                    ? {
                        label:
                          t[
                            'com.streamlain.rootAppSidebar.organize.create.folder'
                          ](),
                        onSelect: handleCreatePrivateFolderImmediately,
                      }
                    : undefined,
                }),
                testId: 'navigation-panel-private-create-button',
                tooltip:
                  t['com.streamlain.rootAppSidebar.organize.create.tooltip'](),
              }}
              options={{
                sortMode: privateRootSortMode,
                onSortModeChange: setPrivateRootSortMode,
                testId: 'navigation-panel-private-options-button',
              }}
            />
          ) : null
        }
      >
        <div
          ref={privateRootContentDropTargetRef}
          className={rootDropZone}
          data-testid="navigation-panel-private-root-content"
          data-private-root-id={privateRootId ?? ''}
          data-private-root-item-count={orderedPrivateRootItems.length}
          data-accessible-folder-count={accessibleFolders.length}
        >
          <NavigationPanelSidebarSortModeProvider mode={privateRootSortMode}>
            <NavigationPanelTreeRoot
              placeholder={
                privateRoot ? (
                  <SidebarCreatePlaceholder
                    ref={privateRootEmptyDropTargetRef}
                    icon={<PrivateFolderIcon />}
                    message={t[
                      'com.streamlain.rootAppSidebar.organize.empty-folder'
                    ]()}
                    messageTestId="slider-bar-private-empty-message"
                    actionText={
                      canCreatePrivateFolder
                        ? t[
                            'com.streamlain.rootAppSidebar.organize.empty.new-folders-button'
                          ]()
                        : undefined
                    }
                    onActionClick={
                      canCreatePrivateFolder
                        ? handleCreatePrivateFolderImmediately
                        : undefined
                    }
                  />
                ) : null
              }
            >
              {showMailFolder && mailFolder && visibleMailDocIds.length ? (
                <MailFolderNode
                  folder={mailFolder}
                  docIds={visibleMailDocIds}
                  docTitleById={docTitleById}
                  parentPath={['private']}
                />
              ) : null}
              {orderedPrivateRootItems.map(item =>
                item.type === 'doc' ? (
                  <NavigationPanelDocNode
                    key={item.key}
                    docId={item.docId}
                    hideLinkedChildren
                    location={{
                      at: 'navigation-panel:private:root-doc',
                      nodeId: item.docId,
                    }}
                    canDrop={privateRootCanDrop}
                    onDrop={data => {
                      const position = getPrivateDropPosition(
                        data,
                        'doc',
                        item.docId
                      );
                      if (!position) {
                        return;
                      }
                      handlePrivateRootChildDrop(data, position);
                    }}
                    dropEffect={privateFolderDropEffect}
                    parentPath={['private']}
                  />
                ) : (
                  <PrivateUserFolderNode
                    key={item.key}
                    folder={item.folder}
                    allFolders={accessibleFolders}
                    newFolderId={newFolderId}
                    setNewFolderId={setNewFolderId}
                    parentPath={['private']}
                    allowSiblingDrop={privateRootSortMode === 'manual'}
                    onMoveToFolder={moveEntityToPrivateFolder}
                  />
                )
              )}
            </NavigationPanelTreeRoot>
          </NavigationPanelSidebarSortModeProvider>
        </div>
      </CollapsibleSection>
    </>
  );
};

const formatSidebarBadgeCount = (
  count: number,
  maxCount = SIDEBAR_BADGE_MAX_COUNT
) => (count > maxCount ? `${maxCount}+` : String(count));

const SidebarBadge = ({
  className,
  count,
  label,
}: {
  className?: string;
  count: number;
  label?: string;
}) => {
  if (count === 0) return null;
  return (
    <div className={clsx(notificationButtonStyles.badge, className)}>
      {label ?? formatSidebarBadgeCount(count)}
    </div>
  );
};

type SidebarHeaderBadgeAction = {
  key: string;
  count: number;
  icon: ReactElement<SVGAttributes<SVGElement>>;
  label: string;
  testId: string;
  badgeCaps?: readonly number[];
  onClick?: () => void;
  wrapButton?: (button: ReactElement) => ReactNode;
};

const resolveSidebarHeaderBadgeLabels = ({
  actions,
  availableWidth,
}: {
  actions: SidebarHeaderBadgeAction[];
  availableWidth: number | null;
}) => {
  const visibleActions = actions.filter(action => action.count > 0);
  const width = availableWidth ?? Number.POSITIVE_INFINITY;

  if (visibleActions.length === 0) {
    return {};
  }

  const candidates = visibleActions.reduce<
    Array<{ labels: Record<string, string>; rank: number; width: number }>
  >(
    (partialCandidates, action, actionIndex) =>
      partialCandidates.flatMap(candidate =>
        (action.badgeCaps ?? [SIDEBAR_BADGE_MAX_COUNT]).map((cap, capIndex) => {
          const label = formatSidebarBadgeCount(action.count, cap);
          const labelWidth =
            (actionIndex === 0 ? 0 : WORKSPACE_HEADER_SUMMARY_ITEM_GAP) +
            SIDEBAR_MENU_ITEM_ICON_SIZE +
            WORKSPACE_HEADER_SUMMARY_ICON_BADGE_GAP +
            WORKSPACE_HEADER_SUMMARY_BADGE_HORIZONTAL_PADDING +
            label.length * WORKSPACE_HEADER_SUMMARY_BADGE_CHARACTER_WIDTH;

          return {
            labels: {
              ...candidate.labels,
              [action.key]: label,
            },
            rank: candidate.rank + capIndex,
            width: candidate.width + labelWidth,
          };
        })
      ),
    [{ labels: {}, rank: 0, width: 0 }]
  );

  return (
    candidates
      .filter(candidate => candidate.width <= width)
      .sort((a, b) => a.rank - b.rank || b.width - a.width)[0]?.labels ??
    candidates[candidates.length - 1].labels
  );
};

const SidebarHeaderBadgeSummary = ({
  actions,
}: {
  actions: SidebarHeaderBadgeAction[];
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const availableWidth = useElementWidth(ref);
  const visibleActions = useMemo(
    () => actions.filter(action => action.count > 0),
    [actions]
  );
  const labels = useMemo(
    () =>
      resolveSidebarHeaderBadgeLabels({
        actions: visibleActions,
        availableWidth,
      }),
    [availableWidth, visibleActions]
  );

  if (visibleActions.length === 0) return null;

  return (
    <div ref={ref} className={workspaceHeaderSummary}>
      {visibleActions.map(action => {
        const icon = cloneElement(action.icon, {
          className: clsx([
            workspaceHeaderSummaryIcon,
            action.icon.props.className,
          ]),
        });
        const button = (
          <button
            type="button"
            aria-label={action.label}
            className={workspaceHeaderSummaryItem}
            data-testid={action.testId}
            title={action.label}
            onClick={action.onClick}
          >
            {icon}
            <SidebarBadge
              className={workspaceHeaderSummaryBadge}
              count={action.count}
              label={labels[action.key]}
            />
          </button>
        );
        return (
          <Fragment key={action.key}>
            {action.wrapButton ? action.wrapButton(button) : button}
          </Fragment>
        );
      })}
    </div>
  );
};

const useElementWidth = (ref: RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => {
      setWidth(element.getBoundingClientRect().width);
    };
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return width;
};

const useWorkspaceMailUnreadCount = (workspaceId: string | null) => {
  const graphql = useService(GraphQLService);
  const { data: mailFolders } = useSWR(
    workspaceId ? ['sidebar-mail-unread', workspaceId] : null,
    async ([, currentWorkspaceId]) => {
      const response = await graphql.gql({
        query: mailFoldersQuery,
        variables: {
          workspaceId: currentWorkspaceId,
          accountId: null,
        },
      });
      return response.mailFolders;
    },
    { suspense: false }
  );

  return useMemo(
    () =>
      (mailFolders ?? []).reduce(
        (total, folder) => total + folder.unreadCount,
        0
      ),
    [mailFolders]
  );
};

const toLocalTrashedFolderSummary = (folder: {
  id: string;
  docIds: string[];
}): Pick<AccessibleTrashedFolderMeta, 'id' | 'docIds'> => ({
  id: folder.id,
  docIds: folder.docIds,
});

const useSidebarTrashCount = () => {
  const workspace = useService(WorkspaceService).workspace;
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderApiService = useService(FolderApiService);
  const folderStore = useService(FolderStore);
  const isCloudWorkspace = workspace.flavour !== 'local';
  const trashedDocIds = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleDocCache.watchTrashedDocIds(),
          accessibleDocCache
            .getCachedDocs()
            .filter(doc => doc.trashed)
            .map(doc => doc.id)
        ),
      [accessibleDocCache]
    )
  );
  useEffect(() => {
    markSidebarPerformanceSignal('source:trashed-doc-ids');
  }, [trashedDocIds]);
  const [trashedFolders, setTrashedFolders] = useState<
    Pick<AccessibleTrashedFolderMeta, 'id' | 'docIds'>[]
  >([]);

  const refreshTrashedFolders = useCallback(() => {
    if (!isCloudWorkspace) return;
    void accessibleFolderCache
      .fetchTrashedFolders({ force: true })
      .catch(() => {
        // Keep the persisted local-first trash shape while offline.
      });
  }, [accessibleFolderCache, isCloudWorkspace]);

  useEffect(() => {
    if (isCloudWorkspace) {
      const subscription = accessibleFolderCache
        .watchTrashedFolderMetadata$()
        .subscribe(setTrashedFolders);
      refreshTrashedFolders();
      return () => subscription.unsubscribe();
    }
    const subscription = folderStore
      .watchTrashedFolders$()
      .subscribe(folders => {
        setTrashedFolders(folders.map(toLocalTrashedFolderSummary));
      });
    return () => subscription.unsubscribe();
  }, [
    accessibleFolderCache,
    folderStore,
    isCloudWorkspace,
    refreshTrashedFolders,
  ]);

  useEffect(() => {
    const subscription = folderApiService.folderTrashChanged$.subscribe(() => {
      refreshTrashedFolders();
    });
    return () => subscription.unsubscribe();
  }, [folderApiService, refreshTrashedFolders]);

  return useMemo(() => {
    const folderDocIds = new Set(trashedFolders.flatMap(f => f.docIds));
    const trashedDocCount = trashedDocIds.filter(
      docId => !folderDocIds.has(docId)
    ).length;
    return trashedFolders.length + trashedDocCount;
  }, [trashedDocIds, trashedFolders]);
};

const WorkspaceSectionHeaderSummary = ({
  mailCount,
  notificationCount,
}: {
  mailCount: number;
  notificationCount: number;
}) => {
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const t = useI18n();
  const notificationLabel = t['com.streamlain.rootAppSidebar.notifications']();
  const mailLabel = getMailAppLabel(t);
  const handleOpenMail = useCallback(() => {
    workbench.open('/mail');
  }, [workbench]);
  const actions = useMemo<SidebarHeaderBadgeAction[]>(
    () => [
      {
        key: 'notifications',
        count: notificationCount,
        icon: <NotificationIcon />,
        label: notificationLabel,
        testId: 'workspace-header-notifications-button',
        badgeCaps: WORKSPACE_HEADER_NOTIFICATION_BADGE_CAPS,
        wrapButton: button => (
          <NotificationListPopover>
            {() => button as ReactElement<{ ref?: Ref<HTMLElement> }>}
          </NotificationListPopover>
        ),
      },
      {
        key: 'mail',
        count: mailCount,
        icon: <EmailIcon />,
        label: mailLabel,
        testId: 'workspace-header-mail-button',
        badgeCaps: WORKSPACE_HEADER_MAIL_BADGE_CAPS,
        onClick: handleOpenMail,
      },
    ],
    [handleOpenMail, mailCount, mailLabel, notificationCount, notificationLabel]
  );

  return <SidebarHeaderBadgeSummary actions={actions} />;
};

const MailInboxButton = ({ workspaceId }: { workspaceId: string }) => {
  const { workbenchService } = useServices({ WorkbenchService });
  const workbench = workbenchService.workbench;
  const t = useI18n();
  const mailActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/mail')
  );
  const unreadCount = useWorkspaceMailUnreadCount(workspaceId);

  return (
    <MenuLinkItem
      icon={<EmailIcon />}
      active={mailActive}
      to="/mail"
      data-testid="sidebar-mail-inbox"
      postfix={<SidebarBadge count={unreadCount} />}
      postfixDisplay="always"
      onClick={() => {
        if (mailActive) {
          requestNativeWorkspaceAppSidebar('mail');
        }
      }}
    >
      <span>{getMailAppLabel(t)}</span>
    </MenuLinkItem>
  );
};

const MailFolderNode = ({
  folder,
  docIds,
  docTitleById,
  parentPath,
  label,
  locked = true,
  testId = 'sidebar-private-mail-folder',
}: {
  folder: AccessibleFolderMeta;
  docIds: readonly string[];
  docTitleById: ReadonlyMap<string, string>;
  parentPath: string[];
  label?: string;
  locked?: boolean;
  testId?: string;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const mailAccounts = useWorkspaceMailAccounts(
    workspace.flavour === 'local' ? null : workspace.id
  );
  const accountLabelByDocId = useMemo(
    () =>
      new Map(
        (mailAccounts.data ?? []).map(account => [
          account.accessDocId,
          account.emailAddress,
        ])
      ),
    [mailAccounts.data]
  );
  const navigationPanelService = useService(NavigationPanelService);
  const path = useMemo(
    () => [...parentPath, `folder-${folder.id}`],
    [folder.id, parentPath]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  return (
    <NavigationPanelTreeNode
      icon={MailFolderIcon}
      name={label ?? t['com.streamlain.rootAppSidebar.privateFolder.mail']()}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      postfix={locked ? <LockIcon /> : undefined}
      data-testid={testId}
      explorerIconConfig={{
        where: 'folder',
        id: folder.id,
      }}
      reorderable={false}
      canDrop={false}
    >
      {docIds.map(docId => (
        <MailDocTreeNode
          key={docId}
          docId={docId}
          name={
            accountLabelByDocId.get(docId) ??
            docTitleById.get(docId) ??
            undefined
          }
          locked={locked}
        />
      ))}
    </NavigationPanelTreeNode>
  );
};

const MailDocTreeNode = ({
  docId,
  locked = true,
  name,
}: {
  docId: string;
  locked?: boolean;
  name?: string;
}) => {
  const { workbenchService } = useServices({ WorkbenchService });
  const t = useI18n();
  const workbench = workbenchService.workbench;
  const personalDocActive = useLiveData(
    workbench.location$.selector(location => location.pathname === `/${docId}`)
  );
  const openPersonalDoc = useCallback(() => {
    workbench.openDoc(docId, { at: 'active' });
  }, [docId, workbench]);

  return (
    <NavigationPanelTreeNode
      icon={MailDocIcon}
      name={name || t['com.streamlain.rootAppSidebar.privateFolder.mailDoc']()}
      active={personalDocActive}
      disabled={false}
      onClick={openPersonalDoc}
      to={`/${docId}`}
      collapsed
      setCollapsed={() => {}}
      collapsible={false}
      postfix={locked ? <LockIcon /> : undefined}
      reorderable={false}
      canDrop={false}
      data-testid={locked ? 'sidebar-mail-personal-doc' : 'sidebar-mail-doc'}
    />
  );
};

function asSidebarRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function getConfigStringField(config: unknown, field: string): string | null {
  const value = asSidebarRecord(config)?.[field];
  return typeof value === 'string' && value.trim() ? value : null;
}

function useAppAreaSidebarAvailability() {
  const workspace = useService(WorkspaceService).workspace;
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const { isFeatureAllowed } = useFeatureGate();
  const shareSetting = useService(WorkspaceShareSettingService).sharePreview;
  const customAppsEnabled = useLiveData(shareSetting.customAppsEnabled$);
  const hasCustomApps = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const appAreaRoot = useMemo(
    () =>
      accessibleFolders.find(
        folder =>
          folder.systemKind === APP_AREA_ROOT_SYSTEM_KIND &&
          folder.parentId === null
      ) ?? null,
    [accessibleFolders]
  );
  const canLoadAppArea =
    workspace.flavour !== 'local' &&
    hasCustomApps &&
    customAppsEnabled === true;

  return {
    accessibleFolders,
    appAreaRoot,
    canLoadAppArea,
  };
}

const AppAreaSidebarContent = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const graphql = useService(GraphQLService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const canViewCustomApps = useGuard('Workspace_CustomApps_View') === true;
  const { accessibleFolders, appAreaRoot, canLoadAppArea } =
    useAppAreaSidebarAvailability();
  const accessibleDocs = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleDocCache.watchAllAccessibleDocs(),
          accessibleDocCache.getCachedDocs()
        ),
      [accessibleDocCache]
    )
  );

  const { views, isValidating: customAppsValidating } = useWorkspaceCustomApps(
    canLoadAppArea && (canViewCustomApps || appAreaRoot) ? workspace.id : null
  );
  const appAreaRootId = appAreaRoot?.id ?? null;

  useEffect(() => {
    if (!canLoadAppArea) {
      return;
    }
    accessibleFolderCache.fetchAccessibleFolders().catch(error => {
      console.error('Failed to load app area folders:', error);
    });
    accessibleDocCache.fetchAccessibleDocs().catch(error => {
      console.error('Failed to load app area docs:', error);
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    canLoadAppArea,
    customAppsValidating,
    views?.length,
  ]);
  useEffect(() => {
    if (!canLoadAppArea || !appAreaRootId) {
      return;
    }
    accessibleFolderCache
      .fetchFolderChildren(appAreaRootId, {
        diagnosticReason: 'app-area-root',
        force: true,
        reuseRecent: true,
      })
      .catch(error => {
        console.error('Failed to load app folders:', error);
      });
  }, [
    accessibleFolderCache,
    appAreaRootId,
    canLoadAppArea,
    customAppsValidating,
    views?.length,
  ]);
  const appByFolderId = useMemo(() => {
    const next = new Map<string, { id: string; docId: string }>();
    for (const view of views ?? []) {
      const appFolderId = getConfigStringField(view.config, 'appFolderId');
      if (appFolderId) {
        next.set(appFolderId, { id: view.id, docId: view.docId });
      }
    }
    return next;
  }, [views]);
  const appRootFolders = useMemo(
    () =>
      appAreaRoot
        ? accessibleFolders
            .filter(
              folder =>
                folder.parentId === appAreaRoot.id &&
                folder.systemKind === CUSTOM_APP_FOLDER_SYSTEM_KIND
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [accessibleFolders, appAreaRoot]
  );

  if (!canLoadAppArea || !appAreaRoot) {
    return null;
  }

  return (
    <NavigationPanelTreeRoot
      placeholder={
        appRootFolders.length === 0 ? (
          <NavigationPanelEmptySection
            icon={<StreamLainCustomAppIcon />}
            message={t['com.streamlain.rootAppSidebar.appArea.empty']()}
          />
        ) : null
      }
    >
      {appRootFolders.map(folder => (
        <AppAreaFolderNode
          key={folder.id}
          folder={folder}
          allFolders={accessibleFolders}
          accessibleDocs={accessibleDocs}
          parentPath={['app-area']}
          rootDocId={appByFolderId.get(folder.id)?.docId ?? null}
          customAppId={appByFolderId.get(folder.id)?.id ?? null}
          onTrashCustomApp={async customAppId => {
            await graphql.gql({
              query: deleteCustomAppMutation,
              variables: { workspaceId: workspace.id, id: customAppId },
            });
            await Promise.all([
              accessibleFolderCache.fetchAccessibleFolders(),
              accessibleDocCache.fetchAccessibleDocs(),
            ]);
          }}
        />
      ))}
    </NavigationPanelTreeRoot>
  );
};

const AppAreaFolderNode = ({
  folder,
  allFolders,
  accessibleDocs,
  parentPath,
  rootDocId,
  customAppId,
  onTrashCustomApp,
}: {
  folder: AccessibleFolderMeta;
  allFolders: AccessibleFolderMeta[];
  accessibleDocs: Array<{ id: string; title: string; trashed: boolean }>;
  parentPath: string[];
  rootDocId: string | null;
  customAppId: string | null;
  onTrashCustomApp: (customAppId: string) => Promise<void>;
}) => {
  const t = useI18n();
  const navigationPanelService = useService(NavigationPanelService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const { openConfirmModal } = useConfirmModal();
  const path = useMemo(
    () => [...parentPath, `folder-${folder.id}`],
    [folder.id, parentPath]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  useEffect(() => {
    void accessibleFolderCache
      .fetchFolderChildren(folder.id, {
        diagnosticReason: 'app-area-folder',
        force: true,
        reuseRecent: true,
      })
      .catch(() => {});
  }, [accessibleFolderCache, folder.id]);
  const docTitleById = useMemo(
    () =>
      new Map(
        accessibleDocs
          .filter(doc => !doc.trashed)
          .map(doc => [doc.id, doc.title])
      ),
    [accessibleDocs]
  );
  const childFolders = useMemo(
    () =>
      allFolders
        .filter(child => child.parentId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allFolders, folder.id]
  );
  const childItems = useMemo<AppAreaSidebarChildItem[]>(
    () =>
      [
        ...childFolders.map(child => ({
          key: sidebarManualOrderKey('folder', child.id),
          label: child.name,
          type: 'folder' as const,
          folder: child,
        })),
        ...(folder.docIds ?? [])
          .filter(docId => docTitleById.has(docId))
          .map(docId => ({
            key: sidebarManualOrderKey('doc', docId),
            label: docTitleById.get(docId) ?? '',
            type: 'doc' as const,
            docId,
          })),
      ].sort(compareSidebarManualOrderItems),
    [childFolders, docTitleById, folder.docIds]
  );
  const operations = useMemo(
    () => [
      {
        index: 200,
        view: (
          <ComponentMenuItem
            data-testid="app-folder-menu-configure"
            prefixIcon={<SettingsIcon />}
            onClick={() => setShareModalOpen(true)}
          >
            {t['com.streamlain.rootAppSidebar.appArea.configure']()}
          </ComponentMenuItem>
        ),
      },
      ...(customAppId
        ? [
            {
              index: 9999,
              view: (
                <ComponentMenuItem
                  data-testid="app-folder-menu-trash"
                  prefixIcon={<DeleteIcon />}
                  type="danger"
                  onClick={() =>
                    openConfirmModal({
                      title: t['com.streamlain.moveToTrash.title'](),
                      description: t[
                        'com.streamlain.moveToTrash.confirmModal.description'
                      ]({ title: folder.name }),
                      cancelText: t['Cancel'](),
                      confirmText: t['com.streamlain.moveToTrash.title'](),
                      confirmButtonOptions: { variant: 'error' },
                      onConfirm: () => void onTrashCustomApp(customAppId),
                    })
                  }
                >
                  {t['com.streamlain.moveToTrash.title']()}
                </ComponentMenuItem>
              ),
            },
          ]
        : []),
    ],
    [customAppId, folder.name, onTrashCustomApp, openConfirmModal, t]
  );

  return (
    <>
      <NavigationPanelTreeNode
        icon={PrivateFolderIcon}
        name={folder.name}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        operations={operations}
        revealOperations
        reorderable={false}
        canDrop={false}
        childrenEmpty={isFolderKnownEmpty(
          folder.hasChildren,
          childItems.length
        )}
        childrenPlaceholder={
          folder.hasChildren === true ? undefined : <FolderEmpty />
        }
        data-testid={`navigation-panel-app-folder-${folder.id}`}
        explorerIconConfig={{
          where: 'folder',
          id: folder.id,
        }}
      >
        {childItems.map(item =>
          item.type === 'doc' ? (
            <NavigationPanelDocNode
              key={item.key}
              docId={item.docId}
              icon={PageIcon}
              hideLinkedChildren
              hideTrashOperation={item.docId === rootDocId}
              disableDrag={item.docId === rootDocId}
              reorderable={false}
              canDrop={false}
              parentPath={path}
            />
          ) : (
            <AppAreaFolderNode
              key={item.key}
              folder={item.folder}
              allFolders={allFolders}
              accessibleDocs={accessibleDocs}
              parentPath={path}
              rootDocId={null}
              customAppId={null}
              onTrashCustomApp={onTrashCustomApp}
            />
          )
        )}
      </NavigationPanelTreeNode>
      {shareModalOpen ? (
        <FolderShareModal
          folderId={folder.id}
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      ) : null}
    </>
  );
};

const PrivateUserFolderNode = ({
  folder,
  allFolders,
  newFolderId,
  setNewFolderId,
  parentPath,
  allowSiblingDrop = true,
  onMoveToFolder,
}: {
  folder: AccessibleFolderMeta;
  allFolders: AccessibleFolderMeta[];
  newFolderId: string | null;
  setNewFolderId: (folderId: string | null) => void;
  parentPath: string[];
  allowSiblingDrop?: boolean;
  onMoveToFolder: (
    data: DropTargetDropEvent<AffineDNDData>,
    targetFolderId: string,
    position?: PrivateDropPosition
  ) => Promise<void>;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const connectivityStatus = useConnectivityStatus();
  const navigationPanelService = useService(NavigationPanelService);
  const areaSortMode = useNavigationPanelSidebarSortMode();
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const folderApiService = useService(FolderApiService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderStore = useService(FolderStore);
  const showFolderTrashToast = useTrashFolderToast();
  const authService = useService(AuthService);
  const pageHelper = usePageHelper(workspace.docCollection);
  const account = useLiveData(authService.session.account$);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const path = useMemo(
    () => [...parentPath, `folder-${folder.id}`],
    [folder.id, parentPath]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const setCollapsed = useCallback(
    (value: boolean) => navigationPanelService.setCollapsed(path, value),
    [navigationPanelService, path]
  );

  const childFolders = useMemo(
    () =>
      allFolders.filter(
        child =>
          child.parentId === folder.id && child.systemKind !== 'private_mail'
      ),
    [allFolders, folder.id]
  );
  const docIds = useMemo(() => folder.docIds ?? [], [folder.docIds]);
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const childItems = useMemo<PrivateSidebarChildItem[]>(
    () => [
      ...docIds.map(docId => ({
        key: sidebarManualOrderKey('doc', docId),
        label: docTitleById.get(docId) ?? '',
        type: 'doc' as const,
        docId,
      })),
      ...childFolders.map(child => ({
        key: sidebarManualOrderKey('folder', child.id),
        label: child.name,
        type: 'folder' as const,
        folder: child,
      })),
    ],
    [childFolders, docIds, docTitleById]
  );
  const {
    orderedItems: orderedChildItems,
    placeItem: placeChildItem,
    sortMode: childSortMode,
  } = useSidebarManualOrder(
    `streamlain:private-sidebar-manual-order:${workspace.id}:${folder.id}`,
    childItems,
    compareSidebarManualOrderItems,
    { sortMode: areaSortMode ?? undefined }
  );

  useEffect(() => {
    if (collapsed !== false || folder.hasChildren !== true) {
      return;
    }

    void accessibleFolderCache
      .fetchFolderChildren(folder.id, {
        diagnosticReason: 'private-sidebar-expanded',
      })
      .catch(() => {});
  }, [accessibleFolderCache, collapsed, folder.hasChildren, folder.id]);

  const refreshPrivateFolders = useCallback(async () => {
    await accessibleFolderCache.fetchAccessibleFolders();
  }, [accessibleFolderCache]);

  const applyDocPermissions = useCallback(
    (docId: string, config: DocCreateConfig) => {
      void objectPermissionMutation
        .grantPendingAssignments({ id: docId, kind: 'doc' }, config)
        .catch(error => {
          console.error('Failed to apply private document permissions:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
    },
    [objectPermissionMutation, t]
  );

  const handleNewDoc = useCallback(
    (config: DocCreateConfig) => {
      const newDoc = pageHelper.createPage(undefined, {
        show: false,
        targetFolderId: folder.id,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(folder.id, newDoc.id);
      if (childSortMode === 'manual') {
        placeChildItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      applyDocPermissions(newDoc.id, config);
      void refreshPrivateFolders();
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId: workspace.id,
      });
    },
    [
      applyDocPermissions,
      accessibleFolderCache,
      childSortMode,
      folder.id,
      navigationPanelRevealService,
      pageHelper,
      placeChildItem,
      refreshPrivateFolders,
      workspace.id,
    ]
  );

  const handleCreateDocImmediately = useCallback(() => {
    handleNewDoc(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleNewDoc, t]);

  const handleCreateSubfolder = useCallback(
    (config: FolderCreateConfig) => {
      const folderId = nanoid();
      const snapshot = accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticPrivateFolderMeta({
          id: folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          parentId: folder.id,
          ownerUserId: account?.id ?? null,
          visibility: config.visibility,
        }),
        { preserveUntilServerAck: true }
      );
      if (childSortMode === 'manual') {
        placeChildItem(sidebarManualOrderKey('folder', folderId));
      }
      setNewFolderId(folderId);
      setCollapsed(false);

      void folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          name: config.name,
          parentId: folder.id,
          visibility: config.visibility,
          inheritPermissions: config.inheritPermissions,
        })
        .then(() =>
          enqueueFolderCreatePermissionGrants(
            objectPermissionMutation,
            folderId,
            config
          ).catch(error => {
            console.error(
              'Failed to apply private subfolder permissions:',
              error
            );
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.folder-permissions-failed'
                ](),
            });
          })
        )
        .catch(error => {
          console.error('Failed to create private subfolder:', error);
          accessibleFolderCache.restoreOptimisticSnapshot(snapshot);
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.createFailed'](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      childSortMode,
      folder.id,
      folderMutationOutbox,
      objectPermissionMutation,
      placeChildItem,
      setCollapsed,
      setNewFolderId,
      t,
    ]
  );

  const handleCreateSubfolderImmediately = useCallback(() => {
    handleCreateSubfolder(
      createDefaultFolderCreateConfig(
        'private-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleCreateSubfolder, t]);

  const handleRename = useCallback(
    async (name: string) => {
      const nextName = name.trim();
      if (!nextName || nextName === folder.name) {
        return;
      }
      const snapshot = accessibleFolderCache.captureOptimisticSnapshot();
      accessibleFolderCache.optimisticRenameFolder(folder.id, nextName);
      try {
        await folderMutationOutbox.enqueueRenameFolder(folder.id, nextName);
        setNewFolderId(null);
      } catch (error) {
        accessibleFolderCache.restoreOptimisticSnapshot(snapshot);
        throw error;
      }
    },
    [
      accessibleFolderCache,
      folder.id,
      folder.name,
      folderMutationOutbox,
      setNewFolderId,
    ]
  );

  const handleTrashFolder = useCallback(() => {
    const isCloudWorkspace = workspace.flavour !== 'local';
    void moveFolderToTrashLocalFirstAction({
      accessibleDocCache,
      accessibleFolderCache,
      folderApiService,
      folderId: folder.id,
      folderStore,
      canTrashFolder: true,
      canUseYjsFolderRoute: !isCloudWorkspace,
      canUseServerDestructiveLifecycle: () =>
        !isCloudWorkspace || connectivityStatus.backendAvailable,
      workspaceId: workspace.id,
      notifyConnectionRequired: () => {
        notify.error({
          title: t['com.streamlain.settings.connection-required.title'](),
          message:
            t['com.streamlain.settings.connection-required.description'](),
        });
      },
      notifyMoveFailed: () => {
        notify.error({
          title: t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
        });
      },
      notifyRestoreFailed: () => {
        notify.error({
          title:
            t['com.streamlain.rootAppSidebar.privateFolder.restoreFailed'](),
        });
      },
      showFolderTrashToast,
      visibility: 'private',
      logError: console.error,
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    connectivityStatus.backendAvailable,
    folder.id,
    folderApiService,
    folderStore,
    showFolderTrashToast,
    t,
    workspace.flavour,
    workspace.id,
  ]);

  const operations = useMemo(
    () => [
      {
        index: 0,
        inline: true,
        view: (
          <OrganizeCreateMenu
            actions={buildSidebarCreateActions({
              document: {
                label:
                  t['com.streamlain.rootAppSidebar.organize.create.document'](),
                onSelect: handleCreateDocImmediately,
              },
              folder: {
                label:
                  t['com.streamlain.rootAppSidebar.organize.create.folder'](),
                onSelect: handleCreateSubfolderImmediately,
              },
            })}
            testId="private-folder-create-button"
            tooltip={t[
              'com.streamlain.rootAppSidebar.organize.create.tooltip'
            ]()}
          />
        ),
      },
      {
        index: 200,
        view: (
          <ComponentMenuItem
            data-testid="private-folder-menu-configure"
            prefixIcon={<SettingsIcon />}
            onClick={() => setShareModalOpen(true)}
          >
            {t['com.streamlain.streamlain.common.configure']()}
          </ComponentMenuItem>
        ),
      },
      {
        index: 9999,
        view: (
          <ComponentMenuItem
            data-testid="private-folder-menu-trash"
            prefixIcon={<DeleteIcon />}
            type="danger"
            onClick={handleTrashFolder}
          >
            {t['com.streamlain.moveToTrash.title']()}
          </ComponentMenuItem>
        ),
      },
    ],
    [
      handleCreateDocImmediately,
      handleCreateSubfolderImmediately,
      handleTrashFolder,
      t,
    ]
  );

  const dndData = useMemo(
    () =>
      ({
        draggable: {
          entity: {
            type: 'folder',
            id: folder.id,
          },
          folderTrash: {
            canTrashFolder: true,
          },
          from: {
            at: 'navigation-panel:private:folder-node',
            nodeId: folder.id,
            folderName: folder.name,
          },
        },
        dropTarget: {
          at: 'navigation-panel:organize:folder',
        },
      }) satisfies AffineDNDData,
    [folder.id, folder.name]
  );
  const selectionItem = useMemo<NavigationPanelTreeSelectionItem>(
    () => ({
      entity: {
        type: 'folder',
        id: folder.id,
      },
      folderTrash: {
        canTrashFolder: true,
      },
      from: {
        at: 'navigation-panel:private:folder-node',
        nodeId: folder.id,
        folderName: folder.name,
      },
    }),
    [folder.id, folder.name]
  );

  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      if (
        !allowSiblingDrop &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      return canDropPrivateEntity(args, {
        targetFolderId: folder.id,
        allFolders,
        canDropDoc: docArgs => {
          const entity = docArgs.source.data.entity;
          return (
            entity?.type === 'doc' &&
            cachedFolderAllowsDocAdd(workspace.id, folder.id) &&
            cachedFolderAllowsDocRemoval(
              workspace.id,
              accessibleFolderCache,
              entity.id,
              folder.id
            )
          );
        },
      });
    },
    [
      accessibleFolderCache,
      allFolders,
      allowSiblingDrop,
      folder.id,
      workspace.id,
    ]
  );

  const handleChildCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      return canDropPrivateEntity(args, {
        targetFolderId: folder.id,
        allFolders,
        allowMakeChild: false,
        canDropDoc: docArgs => {
          const entity = docArgs.source.data.entity;
          return (
            entity?.type === 'doc' &&
            cachedFolderAllowsDocAdd(workspace.id, folder.id) &&
            cachedFolderAllowsDocRemoval(
              workspace.id,
              accessibleFolderCache,
              entity.id,
              folder.id
            )
          );
        },
      });
    },
    [accessibleFolderCache, allFolders, folder.id, workspace.id]
  );

  const handleDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const position = getPrivateDropPosition(data, 'folder', folder.id);
      const targetFolderId = position ? folder.parentId : folder.id;
      if (!targetFolderId) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          onMoveToFolder(itemData, targetFolderId, position).catch(error => {
            console.error('Failed to move item to private folder:', error);
          });
        });
        return;
      }
      onMoveToFolder(data, targetFolderId, position).catch(error => {
        console.error('Failed to move item to private folder:', error);
      });
    },
    [folder.id, folder.parentId, onMoveToFolder]
  );

  const handleDropOnDoc = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, docId: string) => {
      const position = getPrivateDropPosition(data, 'doc', docId);
      if (!position) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          const entity = itemData.source.data.entity;
          if (entity?.type === 'doc' || entity?.type === 'folder') {
            placeChildItem(
              sidebarManualOrderKey(entity.type, entity.id),
              toSidebarOrderPosition(position)
            );
          }
          onMoveToFolder(itemData, folder.id, position).catch(error => {
            console.error('Failed to move item inside private folder:', error);
          });
        });
        return;
      }
      const entity = data.source.data.entity;
      if (entity?.type === 'doc' || entity?.type === 'folder') {
        placeChildItem(
          sidebarManualOrderKey(entity.type, entity.id),
          toSidebarOrderPosition(position)
        );
      }
      onMoveToFolder(data, folder.id, position).catch(error => {
        console.error('Failed to move item inside private folder:', error);
      });
    },
    [folder.id, onMoveToFolder, placeChildItem]
  );

  const handleDropOnFolder = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, childFolderId: string) => {
      const position = getPrivateDropPosition(data, 'folder', childFolderId);
      if (!position) {
        return;
      }
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          const entity = itemData.source.data.entity;
          if (entity?.type === 'doc' || entity?.type === 'folder') {
            placeChildItem(
              sidebarManualOrderKey(entity.type, entity.id),
              toSidebarOrderPosition(position)
            );
          }
          onMoveToFolder(itemData, folder.id, position).catch(error => {
            console.error('Failed to move item inside private folder:', error);
          });
        });
        return;
      }
      const entity = data.source.data.entity;
      if (entity?.type === 'doc' || entity?.type === 'folder') {
        placeChildItem(
          sidebarManualOrderKey(entity.type, entity.id),
          toSidebarOrderPosition(position)
        );
      }
      onMoveToFolder(data, folder.id, position).catch(error => {
        console.error('Failed to move item inside private folder:', error);
      });
    },
    [folder.id, onMoveToFolder, placeChildItem]
  );

  return (
    <>
      <NavigationPanelTreeNode
        icon={PrivateFolderIcon}
        name={folder.name}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        renameable
        defaultRenaming={folder.id === newFolderId}
        onRenamingChange={renaming => {
          if (!renaming && folder.id === newFolderId) {
            setNewFolderId(null);
          }
        }}
        onRename={name => {
          handleRename(name).catch(error => {
            console.error('Failed to rename private folder:', error);
          });
        }}
        operations={operations}
        revealOperations
        childrenEmpty={isFolderKnownEmpty(
          folder.hasChildren,
          orderedChildItems.length
        )}
        childrenPlaceholder={
          folder.hasChildren === true ? undefined : (
            <FolderEmpty canDrop={handleCanDrop} onDrop={handleDrop} />
          )
        }
        dndData={dndData}
        selectionItem={selectionItem}
        canDrop={handleCanDrop}
        onDrop={handleDrop}
        dropEffect={privateFolderDropEffect}
        data-testid={`navigation-panel-private-folder-${folder.id}`}
        explorerIconConfig={{
          where: 'folder',
          id: folder.id,
        }}
      >
        {orderedChildItems.map(item =>
          item.type === 'doc' ? (
            <NavigationPanelDocNode
              key={item.key}
              docId={item.docId}
              hideLinkedChildren
              location={{
                at: 'navigation-panel:private:folder-node',
                nodeId: folder.id,
              }}
              canDrop={handleChildCanDrop}
              onDrop={data => handleDropOnDoc(data, item.docId)}
              dropEffect={privateFolderDropEffect}
              parentPath={path}
            />
          ) : (
            <PrivateUserFolderNode
              key={item.key}
              folder={item.folder}
              allFolders={allFolders}
              newFolderId={newFolderId}
              setNewFolderId={setNewFolderId}
              parentPath={path}
              allowSiblingDrop
              onMoveToFolder={(data, targetFolderId, position) => {
                if (
                  position?.entityType === 'folder' &&
                  position.entityId === item.folder.id &&
                  targetFolderId === folder.id
                ) {
                  handleDropOnFolder(data, item.folder.id);
                  return Promise.resolve();
                }
                return onMoveToFolder(data, targetFolderId, position);
              }}
            />
          )
        )}
      </NavigationPanelTreeNode>
      {shareModalOpen && (
        <FolderShareModal
          folderId={folder.id}
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      )}
    </>
  );
};

function isFolderDescendant(
  folderId: string,
  possibleAncestorId: string,
  folders: AccessibleFolderMeta[]
): boolean {
  let current = folders.find(folder => folder.id === folderId);
  while (current?.parentId) {
    if (current.parentId === possibleAncestorId) {
      return true;
    }
    current = folders.find(folder => folder.id === current?.parentId);
  }
  return false;
}

interface SidebarCreatePlaceholderProps extends HTMLAttributes<HTMLDivElement> {
  actionText?: string;
  icon:
    | ((props: SVGProps<SVGSVGElement>) => JSX.Element)
    | ReactElement<SVGAttributes<SVGElement>>;
  message: string;
  messageTestId?: string;
  onActionClick?: () => void;
  testId?: string;
}

const SidebarCreatePlaceholder = forwardRef<
  HTMLDivElement,
  SidebarCreatePlaceholderProps
>(function SidebarCreatePlaceholder(
  {
    actionText,
    className,
    icon,
    message,
    messageTestId,
    onActionClick,
    testId,
    ...attrs
  },
  ref
) {
  return (
    <NavigationPanelEmptySection
      className={
        className
          ? `${areaCreatePlaceholder} ${className}`
          : areaCreatePlaceholder
      }
      data-testid={testId}
      ref={ref}
      icon={icon}
      message={message}
      messageTestId={messageTestId}
      actionText={actionText}
      onActionClick={onActionClick}
      {...attrs}
    />
  );
});

export const DocumentsSidebarProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceAreaCache = useService(WorkspaceAreaCacheStore);
  const permissionService = useService(WorkspacePermissionService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const [newAreaRootFolderId, setNewAreaRootFolderId] = useState<string | null>(
    null
  );
  const navigationPanelService = useService(NavigationPanelService);
  const areas = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          workspaceAreaCache.watchAreas(),
          workspaceAreaCache.getCachedAreas()
        ),
      [workspaceAreaCache]
    )
  );
  const role = useLiveData(permissionService.permission.role$);
  useEffect(() => {
    markSidebarPerformanceSignal('source:areas');
  }, [areas]);
  useEffect(() => {
    markSidebarPerformanceSignal('source:permission-role');
  }, [role]);
  const effectiveRole = role ?? workspace.meta.role ?? null;

  useEffect(() => {
    if (workspace.flavour === 'local') {
      return;
    }
    accessibleFolderCache.fetchAccessibleFolders().catch(error => {
      if (!isBackendUnavailableError(error)) {
        console.error('Failed to refresh workspace areas:', error);
      }
    });
    accessibleDocCache.fetchAccessibleDocs().catch(error => {
      if (!isBackendUnavailableError(error)) {
        console.error('Failed to refresh workspace area docs:', error);
      }
    });
  }, [
    accessibleDocCache,
    accessibleFolderCache,
    areas?.length,
    workspace.flavour,
  ]);

  const refreshAreas = useCallback(() => {
    workspaceAreaCache.refresh().catch(error => {
      if (!isBackendUnavailableError(error)) {
        console.error('Failed to refresh workspace areas:', error);
      }
    });
    accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
  }, [accessibleFolderCache, workspaceAreaCache]);
  const canCreateArea =
    workspace.flavour !== 'local' &&
    (effectiveRole === Permission.Owner || effectiveRole === Permission.Admin);
  const createArea = useCallback(async () => {
    try {
      const area = await workspaceAreaCache.createTeamspace(
        t['com.streamlain.rootAppSidebar.teamspace.new-default']()
      );
      setNewAreaRootFolderId(area.rootFolderId);
      navigationPanelService.setCollapsed(['teamAreas'], false);
      navigationPanelService.setCollapsed(
        ['teamspace', area.rootFolderId],
        false
      );
      notify.success({
        title: t['com.streamlain.rootAppSidebar.teamspace.created'](),
      });
    } catch (error) {
      console.error('Failed to create workspace area:', error);
      notify.error({
        title: t['com.streamlain.rootAppSidebar.teamspace.createFailed'](),
      });
    }
  }, [navigationPanelService, t, workspaceAreaCache]);
  const value = useMemo<WorkspaceAreaCreationContextValue>(
    () => ({
      areas: areas ?? [],
      canCreateArea,
      createArea,
      newAreaRootFolderId,
      clearNewAreaRootFolderId: () => setNewAreaRootFolderId(null),
      refreshAreas,
    }),
    [areas, canCreateArea, createArea, newAreaRootFolderId, refreshAreas]
  );

  return (
    <WorkspaceAreaCreationContext.Provider value={value}>
      {children}
    </WorkspaceAreaCreationContext.Provider>
  );
};

const JournalSidebarSection = ({
  revealOnExpand = false,
}: {
  revealOnExpand?: boolean;
} = {}) => {
  const t = useI18n();
  const journalService = useService(JournalService);
  const journalDocIds = useLiveData(journalService.allJournalDocIds$);
  const path = useMemo(() => ['journal'], []);

  return (
    <CollapsibleSection
      path={path}
      testId="sidebar-journal"
      title={t['com.streamlain.journal.sidebar-section-title']()}
      revealOnExpand={revealOnExpand}
    >
      <NavigationPanelTreeRoot>
        {journalDocIds.map(docId => (
          <NavigationPanelDocNode
            key={docId}
            docId={docId}
            disableDrag
            hideLinkedChildren
            parentPath={path}
          />
        ))}
      </NavigationPanelTreeRoot>
    </CollapsibleSection>
  );
};

const PersonalSidebarSections = ({
  collectionsEnabled,
  tagsEnabled,
}: {
  collectionsEnabled: boolean;
  tagsEnabled: boolean;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const journalService = useService(JournalService);
  const journalDates = useLiveData(journalService.allJournalDates$);
  const { isFeatureAllowed } = useFeatureGate();
  const privateAreaVisible =
    workspace.flavour !== 'local' &&
    isFeatureAllowed(FEATURE_KEY_PRIVATE_FOLDERS);
  const journalVisible = journalDates.size > 0;
  const personalSectionItems = useMemo<WorkspaceSidebarAreaSectionItem[]>(
    () => [
      {
        key: 'area:migration-favorites',
        label: t['com.streamlain.rootAppSidebar.migration-data'](),
        render: () => <NavigationPanelMigrationFavorites revealOnExpand />,
      },
      ...(journalVisible
        ? [
            {
              key: 'area:journal',
              label: t['com.streamlain.journal.sidebar-section-title'](),
              render: () => <JournalSidebarSection revealOnExpand />,
            },
          ]
        : []),
      ...(privateAreaVisible
        ? [
            {
              key: 'area:private',
              label: t['com.streamlain.rootAppSidebar.privateFolder'](),
              render: () => (
                <PrivateSidebarSection hideJournalFolder revealOnExpand />
              ),
            },
          ]
        : []),
      ...(tagsEnabled
        ? [
            {
              key: 'area:tags',
              label: t['com.streamlain.rootAppSidebar.tags'](),
              render: () => <NavigationPanelTags revealOnExpand />,
            },
          ]
        : []),
      ...(collectionsEnabled
        ? [
            {
              key: 'area:collections',
              label: t['com.streamlain.rootAppSidebar.collections'](),
              render: () => <NavigationPanelCollections revealOnExpand />,
            },
          ]
        : []),
    ],
    [collectionsEnabled, journalVisible, privateAreaVisible, t, tagsEnabled]
  );

  return (
    <SidebarReorderableList
      className={workspaceSharedAreaGroup}
      scope="root-sidebar:personal-areas"
    >
      {personalSectionItems.map(item => (
        <div className={workspaceAreaSection} key={item.key}>
          {item.render()}
        </div>
      ))}
    </SidebarReorderableList>
  );
};

const TeamAreaSections = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceAreaCreation = useWorkspaceAreaCreation();
  const workspaceAreas = workspaceAreaCreation?.areas;
  const refreshAreas =
    workspaceAreaCreation?.refreshAreas ?? noopRefreshWorkspaceAreas;
  const newAreaRootFolderId =
    workspaceAreaCreation?.newAreaRootFolderId ?? null;
  const clearNewAreaRootFolderId =
    workspaceAreaCreation?.clearNewAreaRootFolderId ??
    noopClearNewWorkspaceArea;

  const teamspaces = useMemo(
    () =>
      (workspaceAreas ?? [])
        .filter(area => area.kind === 'teamspace')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [workspaceAreas]
  );
  const fixedAreaSectionItems = useMemo<WorkspaceSidebarAreaSectionItem[]>(
    () => [
      {
        key: 'area:public',
        label: t['com.streamlain.rootAppSidebar.publicArea'](),
        render: () => <NavigationPanelOrganize />,
      },
    ],
    [t]
  );
  const movableAreaSectionItems = useMemo<WorkspaceSidebarAreaSectionItem[]>(
    () =>
      teamspaces.map(area => ({
        key: `area:teamspace:${area.rootFolderId}`,
        label: area.name,
        render: (reorderActions?: SidebarAreaReorderActions) => (
          <TeamspaceSidebarSection
            area={area}
            defaultRenaming={area.rootFolderId === newAreaRootFolderId}
            onRenamingChange={renaming => {
              if (!renaming && area.rootFolderId === newAreaRootFolderId) {
                clearNewAreaRootFolderId();
              }
            }}
            onRefreshAreas={refreshAreas}
            reorderActions={reorderActions}
          />
        ),
      })),
    [clearNewAreaRootFolderId, newAreaRootFolderId, refreshAreas, teamspaces]
  );
  const {
    orderedItems: orderedMovableAreaItems,
    placeItem: placeMovableAreaSection,
  } = useSidebarManualOrder(
    `streamlain:workspace-area-section-order:${workspace.id}`,
    movableAreaSectionItems,
    compareWorkspaceSidebarAreaSections,
    { defaultSortMode: 'manual', manualBase: 'source' }
  );
  const renderedFixedAreaItems = fixedAreaSectionItems.map(item => (
    <div className={workspaceAreaSection} key={item.key}>
      {item.render()}
    </div>
  ));
  const renderedMovableAreaItems = orderedMovableAreaItems.map(
    (item, index) => {
      const moveUp = () => {
        const before = orderedMovableAreaItems[index - 1];
        if (before) {
          placeMovableAreaSection(item.key, {
            at: 'before',
            key: before.key,
          });
        }
      };
      const moveDown = () => {
        const after = orderedMovableAreaItems[index + 1];
        if (after) {
          placeMovableAreaSection(item.key, {
            at: 'after',
            key: after.key,
          });
        }
      };
      return (
        <SidebarReorderableItem
          canMoveDown={index < orderedMovableAreaItems.length - 1}
          canMoveUp={index > 0}
          className={workspaceAreaSection}
          itemKey={item.key}
          key={item.key}
          label={item.label}
          onMoveDown={moveDown}
          onMoveUp={moveUp}
          onPlace={placeMovableAreaSection}
          scope="root-sidebar:workspace-areas"
          testIdPrefix="workspace-area"
        >
          {() =>
            item.render({
              canMoveDown: index < orderedMovableAreaItems.length - 1,
              canMoveUp: index > 0,
              onMoveDown: moveDown,
              onMoveUp: moveUp,
            })
          }
        </SidebarReorderableItem>
      );
    }
  );
  const renderedAreaItems = [
    ...renderedFixedAreaItems,
    ...(orderedMovableAreaItems.length > 0
      ? [
          <div
            aria-hidden="true"
            className={workspaceAreaSeparatorRow}
            key="area:public-dynamic-separator"
          >
            <div className={workspaceAreaSeparatorLine} />
          </div>,
        ]
      : []),
    ...renderedMovableAreaItems,
  ];

  return (
    <SidebarReorderableList
      className={workspaceSharedAreaGroup}
      scope="root-sidebar:workspace-areas"
    >
      {renderedAreaItems}
    </SidebarReorderableList>
  );
};

const TeamspaceSidebarSection = ({
  area,
  defaultRenaming = false,
  onRenamingChange,
  onRefreshAreas,
  reorderActions,
}: {
  area: WorkspaceAreaRef;
  defaultRenaming?: boolean;
  onRenamingChange?: (renaming: boolean) => void;
  onRefreshAreas: () => void;
  reorderActions?: SidebarAreaReorderActions;
}) => {
  const t = useI18n();
  const { openConfirmModal } = useConfirmModal();
  const workspace = useService(WorkspaceService).workspace;
  const connectivityStatus = useConnectivityStatus();
  const navigationPanelService = useService(NavigationPanelService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const objectPermissionMutation = useService(ObjectPermissionMutationService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderStore = useService(FolderStore);
  const authService = useService(AuthService);
  const account = useLiveData(authService.session.account$);
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const { canCreateDocEffective } = useWorkspaceCreateDocPermission();
  const pageHelper = usePageHelper(workspace.docCollection);
  const selectionContext = useContext(NavigationPanelTreeSelectionContext);
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [renaming, setRenaming] = useState(defaultRenaming);
  const [protectAreaRenameFocus, setProtectAreaRenameFocus] =
    useState(defaultRenaming);
  const workspaceAreaCache = useService(WorkspaceAreaCacheStore);
  const path = useMemo(
    () => ['teamspace', area.rootFolderId],
    [area.rootFolderId]
  );
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const accessibleFolders = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleFolderCache.watchAllAccessibleFolders(),
          accessibleFolderCache.getCachedFolders()
        ),
      [accessibleFolderCache]
    )
  );
  const accessibleDocs = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          accessibleDocCache.watchAllAccessibleDocs(),
          accessibleDocCache.getCachedDocs()
        ),
      [accessibleDocCache]
    )
  );
  const teamspaceRoot = useMemo(
    () =>
      accessibleFolders.find(folder => folder.id === area.rootFolderId) ?? null,
    [accessibleFolders, area.rootFolderId]
  );
  const docTitleById = useMemo(
    () =>
      new Map(
        accessibleDocs
          .filter(doc => !doc.trashed)
          .map(doc => [doc.id, doc.title])
      ),
    [accessibleDocs]
  );
  const teamMailboxFolders = useMemo(
    () =>
      accessibleFolders.filter(
        folder =>
          folder.parentId === area.rootFolderId &&
          folder.systemKind === TEAM_MAILBOXES_FOLDER_SYSTEM_KIND
      ),
    [accessibleFolders, area.rootFolderId]
  );
  const teamMailboxFolderIds = useMemo(
    () => teamMailboxFolders.map(folder => folder.id),
    [teamMailboxFolders]
  );
  useMaterializeFolderChildren(
    accessibleFolderCache,
    teamMailboxFolderIds,
    'root-sidebar:team-mail-folder'
  );
  const childFolders = useMemo(
    () =>
      accessibleFolders
        .filter(
          folder =>
            folder.parentId === area.rootFolderId &&
            folder.systemKind !== TEAMSPACE_ROOT_SYSTEM_KIND &&
            (folder.systemKind !== TEAM_MAILBOXES_FOLDER_SYSTEM_KIND ||
              (folder.docIds ?? []).some(
                docId => !hiddenMailFolderDocIds.has(docId)
              ))
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [accessibleFolders, area.rootFolderId, hiddenMailFolderDocIds]
  );

  useEffect(() => {
    accessibleFolderCache
      .fetchFolderChildren(area.rootFolderId)
      .catch(() => {});
  }, [accessibleFolderCache, area.rootFolderId]);

  const rootItems = useMemo<AppAreaSidebarChildItem[]>(
    () => [
      ...childFolders.map(folder => ({
        key: sidebarManualOrderKey('folder', folder.id),
        label: folder.name,
        type: 'folder' as const,
        folder,
      })),
      ...((teamspaceRoot?.docIds ?? []) as string[])
        .filter(
          docId => docTitleById.has(docId) && !hiddenMailFolderDocIds.has(docId)
        )
        .map(docId => ({
          key: sidebarManualOrderKey('doc', docId),
          label: docTitleById.get(docId) ?? '',
          type: 'doc' as const,
          docId,
        })),
    ],
    [childFolders, docTitleById, hiddenMailFolderDocIds, teamspaceRoot?.docIds]
  );
  const {
    orderedItems,
    placeItem: placeRootItem,
    setSortMode,
    sortMode,
  } = useSidebarManualOrder(
    `streamlain:teamspace-sidebar-manual-order:${workspace.id}:${area.rootFolderId}`,
    rootItems,
    compareSidebarManualOrderItems,
    { manualBase: 'source' }
  );
  const canCreateFolder = area.permissions.Folder_CreateSubfolder;
  const canCreateDocument =
    canCreateDocEffective && area.permissions.Folder_AddDoc;
  const canMoveRootFolder = area.permissions.Folder_CreateSubfolder;
  const canMoveDocToRoot = useCallback(
    (docId: string) =>
      cachedFolderAllowsDocAdd(
        workspace.id,
        area.rootFolderId,
        area.permissions
      ) &&
      cachedFolderAllowsDocRemoval(
        workspace.id,
        accessibleFolderCache,
        docId,
        area.rootFolderId
      ),
    [accessibleFolderCache, area.permissions, area.rootFolderId, workspace.id]
  );
  const canManageUsers =
    area.permissions.Folder_Users_Manage || area.permissions.Folder_Users_Read;
  const canTrashArea =
    area.kind === 'teamspace' &&
    !area.immutable &&
    !area.systemManaged &&
    (area.effectiveRole === FolderRole.Owner ||
      area.effectiveRole === FolderRole.Admin) &&
    connectivityStatus.backendAvailable;

  const handleTrashArea = useCallback(() => {
    openConfirmModal({
      title: t['com.streamlain.moveToTrash.title'](),
      description: t['com.streamlain.moveToTrash.confirmModal.description']({
        title: area.name,
      }),
      confirmText: t['com.streamlain.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.streamlain.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: { variant: 'error' },
      async onConfirm() {
        try {
          await workspaceAreaCache.trashTeamspace(area.rootFolderId);
          onRefreshAreas();
        } catch (error) {
          console.error('Failed to trash teamspace:', error);
          notify.error({
            title:
              t['com.streamlain.settings.workspace.teamspaces.trash-failed'](),
          });
        }
      },
    });
  }, [
    area.name,
    area.rootFolderId,
    onRefreshAreas,
    openConfirmModal,
    t,
    workspaceAreaCache,
  ]);

  useEffect(() => {
    if (defaultRenaming) {
      setProtectAreaRenameFocus(true);
      setRenaming(true);
    }
  }, [defaultRenaming]);

  const handleAreaRenamingChange = useCallback(
    (nextRenaming: boolean) => {
      if (!nextRenaming) {
        setProtectAreaRenameFocus(false);
      }
      setRenaming(nextRenaming);
      onRenamingChange?.(nextRenaming);
    },
    [onRenamingChange]
  );
  const startAreaRename = useCallback(
    (protectInitialFocus: boolean) => {
      setProtectAreaRenameFocus(protectInitialFocus);
      handleAreaRenamingChange(true);
    },
    [handleAreaRenamingChange]
  );
  const handleAreaRename = useCallback(
    (name: string) => {
      const nextName = name.trim();
      if (!nextName || nextName === area.name) return;
      void workspaceAreaCache
        .updateTeamspace(area.rootFolderId, { name: nextName })
        .catch(error => {
          console.error('Failed to rename workspace area:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.rename-failed'
              ](),
          });
        });
    },
    [area.name, area.rootFolderId, t, workspaceAreaCache]
  );

  const handleRootSiblingDrop = useCallback(
    (
      data: DropTargetDropEvent<AffineDNDData>,
      targetKey: string,
      rootPosition: SidebarManualOrderPosition
    ) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleRootSiblingDrop(itemData, targetKey, rootPosition);
        });
        selectionContext?.clearSelection();
        return;
      }

      const entity = data.source.data.entity;
      if (!entity || entity.id === area.rootFolderId) {
        return;
      }
      if (entity.type === 'doc') {
        if (!canMoveDocToRoot(entity.id)) {
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
              ](),
          });
          return;
        }

        const optimisticSnapshot =
          accessibleFolderCache.captureOptimisticSnapshot();
        accessibleFolderCache.optimisticAddDocToFolder(
          area.rootFolderId,
          entity.id,
          rootPosition.key.startsWith('doc:')
            ? {
                at: rootPosition.at,
                docId: targetKey,
              }
            : undefined
        );
        placeRootItem(sidebarManualOrderKey('doc', entity.id), rootPosition);
        navigationPanelService.setCollapsed(path, false);

        void folderMutationOutbox
          .enqueueAddDocToFolder(area.rootFolderId, entity.id)
          .then(onRefreshAreas)
          .catch(error => {
            accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
            console.error('Failed to sync doc move to teamspace root:', error);
            notify.error({
              title:
                t[
                  'com.streamlain.rootAppSidebar.organize.folder.error.move-doc-failed'
                ](),
            });
          });
        return;
      }

      if (entity.type !== 'folder') {
        return;
      }
      if (!canMoveRootFolder) {
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
            ](),
        });
        return;
      }

      const optimisticSnapshot =
        accessibleFolderCache.captureOptimisticSnapshot();
      const previousInfo = folderStore.getYjsFolderNodeInfo(entity.id);
      const folderName =
        accessibleFolderCache.getCachedFolder(entity.id)?.name ??
        previousInfo?.data ??
        t['Folder']();

      try {
        accessibleFolderCache.optimisticMoveFolder(
          entity.id,
          area.rootFolderId,
          {
            name: folderName,
            systemKind: null,
            visibility: area.visibility,
          },
          rootPosition.key.startsWith('folder:')
            ? {
                at: rootPosition.at,
                folderId: targetKey,
              }
            : undefined,
          {
            name: folderName,
            ownerUserId: account?.id ?? null,
            systemKind: null,
            visibility: area.visibility,
          }
        );
        placeRootItem(sidebarManualOrderKey('folder', entity.id), rootPosition);
        navigationPanelService.setCollapsed(path, false);
      } catch (error) {
        accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
        console.error('Failed to move folder to teamspace root:', error);
        notify.error({
          title:
            t[
              'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
            ](),
        });
        return;
      }

      void folderMutationOutbox
        .enqueueMoveFolder(entity.id, area.rootFolderId)
        .then(onRefreshAreas)
        .catch(error => {
          accessibleFolderCache.restoreOptimisticSnapshot(optimisticSnapshot);
          console.error('Failed to sync folder move to teamspace root:', error);
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.move-folder-target-denied'
              ](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      area.rootFolderId,
      area.visibility,
      canMoveDocToRoot,
      canMoveRootFolder,
      folderMutationOutbox,
      folderStore,
      navigationPanelService,
      onRefreshAreas,
      path,
      placeRootItem,
      selectionContext,
      t,
    ]
  );

  const handleRootDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      if (getNavigationPanelDragSelection(data).length > 1) {
        forEachNavigationPanelDragSelectionItem(data, itemData => {
          handleRootDrop(itemData);
        });
        selectionContext?.clearSelection();
        return;
      }
      handleRootSiblingDrop(data, area.rootFolderId, {
        at: 'before',
        key: '',
      });
    },
    [area.rootFolderId, handleRootSiblingDrop, selectionContext]
  );

  const handleRootFolderDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, folderId: string) => {
      if (
        data.treeInstruction?.type !== 'reorder-above' &&
        data.treeInstruction?.type !== 'reorder-below'
      ) {
        return;
      }
      const at =
        data.treeInstruction.type === 'reorder-below' ? 'after' : 'before';
      handleRootSiblingDrop(data, folderId, {
        at,
        key: sidebarManualOrderKey('folder', folderId),
      });
    },
    [handleRootSiblingDrop]
  );

  const handleRootDocDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, docId: string) => {
      if (
        data.treeInstruction?.type !== 'reorder-above' &&
        data.treeInstruction?.type !== 'reorder-below'
      ) {
        return;
      }
      const at =
        data.treeInstruction.type === 'reorder-below' ? 'after' : 'before';
      handleRootSiblingDrop(data, docId, {
        at,
        key: sidebarManualOrderKey('doc', docId),
      });
    },
    [handleRootSiblingDrop]
  );

  const handleRootChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      if (
        sortMode !== 'manual' &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      const entity = args.source.data.entity;
      if (entity?.type === 'doc') {
        return canMoveDocToRoot(entity.id);
      }
      return (
        canMoveRootFolder &&
        entity?.type === 'folder' &&
        entity.id !== area.rootFolderId
      );
    },
    [area.rootFolderId, canMoveDocToRoot, canMoveRootFolder, sortMode]
  );
  const canDropOnTeamspaceArea =
    sortMode !== 'manual' || orderedItems.length === 0;
  const { dropTargetRef: teamspaceRootContentDropTargetRef } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:organize:root' },
        onDrop: handleRootDrop,
        canDrop: args =>
          canDropOnTeamspaceArea &&
          ((typeof handleRootChildrenCanDrop === 'function'
            ? handleRootChildrenCanDrop(args)
            : handleRootChildrenCanDrop) ??
            false),
        allowExternal: true,
      }),
      [canDropOnTeamspaceArea, handleRootChildrenCanDrop, handleRootDrop]
    );
  const { dropTargetRef: teamspaceRootHeaderDropTargetRef } =
    useSidebarAreaHeaderDropTarget({
      canDrop: handleRootChildrenCanDrop,
      collapsed,
      onDrop: handleRootDrop,
      path,
    });

  const handleCreateFolder = useCallback(
    (config: FolderCreateConfig) => {
      if (!canCreateFolder) return;
      const folderId = nanoid();
      accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticPrivateFolderMeta({
          id: folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          ownerUserId: account?.id ?? null,
          parentId: area.rootFolderId,
          visibility: config.visibility,
        }),
        { preserveUntilServerAck: true }
      );
      if (sortMode === 'manual') {
        placeRootItem(sidebarManualOrderKey('folder', folderId));
      }
      setNewFolderId(folderId);
      navigationPanelService.setCollapsed(path, false);
      void folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          inheritPermissions: config.inheritPermissions,
          name: config.name,
          parentId: area.rootFolderId,
          visibility: config.visibility,
        })
        .then(() =>
          enqueueFolderCreatePermissionGrants(
            objectPermissionMutation,
            folderId,
            config
          )
        )
        .then(onRefreshAreas)
        .catch(error => {
          console.error('Failed to create teamspace folder:', error);
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.teamspace.folderCreateFailed'](),
          });
        });
    },
    [
      accessibleFolderCache,
      account?.id,
      area.rootFolderId,
      canCreateFolder,
      folderMutationOutbox,
      navigationPanelService,
      objectPermissionMutation,
      onRefreshAreas,
      path,
      placeRootItem,
      sortMode,
      t,
    ]
  );

  const handleCreateDocument = useCallback(
    (config: DocCreateConfig) => {
      if (!canCreateDocument) return;
      const newDoc = pageHelper.createPage(undefined, {
        show: false,
        targetFolderId: area.rootFolderId,
        title: config.name,
      });
      accessibleFolderCache.optimisticAddDocToFolder(
        area.rootFolderId,
        newDoc.id
      );
      if (sortMode === 'manual') {
        placeRootItem(sidebarManualOrderKey('doc', newDoc.id));
      }
      void objectPermissionMutation
        .grantPendingAssignments({ id: newDoc.id, kind: 'doc' }, config)
        .catch(error => {
          console.error(
            'Failed to apply teamspace document permissions:',
            error
          );
          notify.error({
            title:
              t[
                'com.streamlain.rootAppSidebar.organize.folder.error.doc-permissions-failed'
              ](),
          });
        });
      navigationPanelService.setCollapsed(path, false);
      navigationPanelRevealService.revealDoc(newDoc.id, {
        openAfterRenameSubmit: true,
        rename: true,
        source: 'doc-create',
        workspaceId: workspace.id,
      });
      void onRefreshAreas();
    },
    [
      accessibleFolderCache,
      area.rootFolderId,
      canCreateDocument,
      navigationPanelRevealService,
      navigationPanelService,
      objectPermissionMutation,
      onRefreshAreas,
      pageHelper,
      path,
      placeRootItem,
      sortMode,
      t,
      workspace.id,
    ]
  );

  const handleCreateDocumentImmediately = useCallback(() => {
    handleCreateDocument(createDefaultDocCreateConfig(t['Untitled']()));
  }, [handleCreateDocument, t]);

  const handleCreateFolderImmediately = useCallback(() => {
    handleCreateFolder(
      createDefaultFolderCreateConfig(
        'teamspace-folder',
        t['com.streamlain.rootAppSidebar.organize.create.folder']()
      )
    );
  }, [handleCreateFolder, t]);

  useEffect(() => {
    accessibleFolderCache
      .fetchFolderChildren(area.rootFolderId)
      .catch(() => {});
  }, [accessibleFolderCache, area.rootFolderId]);

  useEffect(() => {
    if (!teamspaceRoot || (teamspaceRoot.docIds ?? []).length === 0) {
      return;
    }
    accessibleDocCache
      .fetchFolderDocs([...(teamspaceRoot.docIds ?? [])])
      .catch(() => {});
  }, [accessibleDocCache, teamspaceRoot]);

  return (
    <>
      <CollapsibleSection
        path={path}
        headerRef={teamspaceRootHeaderDropTargetRef}
        testId="sidebar-teamspace"
        rootDataAttributes={{
          'data-teamspace-root-id': area.rootFolderId,
        }}
        revealOnMount={defaultRenaming}
        titleInteractive
        title={
          renaming ? (
            <NavigationPanelTreeNodeInlineRename
              handleRename={handleAreaRename}
              inputTestId={`rename-area-input-${area.rootFolderId}`}
              protectInitialFocus={protectAreaRenameFocus}
              rawName={area.name}
              setRenaming={handleAreaRenamingChange}
            />
          ) : (
            <span
              onDoubleClick={event => {
                event.preventDefault();
                event.stopPropagation();
                navigationPanelService.setCollapsed(path, false);
                startAreaRename(false);
              }}
            >
              {area.name}
            </span>
          )
        }
        actions={
          <SidebarAreaHeaderActions
            create={{
              actions: buildSidebarCreateActions({
                document: canCreateDocument
                  ? {
                      label:
                        t[
                          'com.streamlain.rootAppSidebar.organize.create.document'
                        ](),
                      onSelect: handleCreateDocumentImmediately,
                    }
                  : undefined,
                folder: canCreateFolder
                  ? {
                      label:
                        t[
                          'com.streamlain.rootAppSidebar.organize.create.folder'
                        ](),
                      onSelect: handleCreateFolderImmediately,
                    }
                  : undefined,
              }),
              testId: 'navigation-panel-teamspace-create-button',
              tooltip:
                t['com.streamlain.rootAppSidebar.organize.create.tooltip'](),
            }}
            options={{
              rename: () => startAreaRename(true),
              managePermissions: canManageUsers
                ? () => setShareModalOpen(true)
                : undefined,
              trash: canTrashArea ? handleTrashArea : undefined,
              reorderActions,
              sortMode,
              onSortModeChange: setSortMode,
              testId: 'navigation-panel-teamspace-options-button',
            }}
          />
        }
      >
        <div
          ref={teamspaceRootContentDropTargetRef}
          className={rootDropZone}
          data-teamspace-root-id={area.rootFolderId}
          data-teamspace-root-present={teamspaceRoot !== null}
          data-accessible-folder-count={accessibleFolders.length}
          data-child-folder-count={childFolders.length}
        >
          <NavigationPanelSidebarSortModeProvider mode={sortMode}>
            <NavigationPanelTreeRoot
              placeholder={
                orderedItems.length === 0 ? (
                  <SidebarCreatePlaceholder
                    icon={<FolderIcon />}
                    message={t[
                      'com.streamlain.rootAppSidebar.organize.empty-folder'
                    ]()}
                    actionText={
                      canCreateFolder
                        ? t[
                            'com.streamlain.rootAppSidebar.organize.empty.new-folders-button'
                          ]()
                        : undefined
                    }
                    onActionClick={
                      canCreateFolder
                        ? handleCreateFolderImmediately
                        : undefined
                    }
                  />
                ) : null
              }
            >
              {orderedItems.map(item =>
                item.type === 'doc' ? (
                  <NavigationPanelDocNode
                    key={item.key}
                    docId={item.docId}
                    hideLinkedChildren
                    onDrop={data => handleRootDocDrop(data, item.docId)}
                    dropEffect={organizeChildrenDropEffect}
                    canDrop={handleRootChildrenCanDrop}
                    location={{
                      at: 'navigation-panel:organize:root-doc',
                      nodeId: item.docId,
                    }}
                    parentPath={path}
                  />
                ) : item.folder.systemKind ===
                  TEAM_MAILBOXES_FOLDER_SYSTEM_KIND ? (
                  <MailFolderNode
                    key={item.key}
                    folder={item.folder}
                    docIds={(item.folder.docIds ?? []).filter(
                      docId => !hiddenMailFolderDocIds.has(docId)
                    )}
                    docTitleById={docTitleById}
                    parentPath={path}
                    label={t[
                      'com.streamlain.rootAppSidebar.privateFolder.mail'
                    ]()}
                    locked={false}
                    testId="sidebar-teamspace-mail-folder"
                  />
                ) : (
                  <NavigationPanelFolderNode
                    key={item.key}
                    nodeId={item.folder.id}
                    defaultRenaming={item.folder.id === newFolderId}
                    onRenamingChange={renaming => {
                      if (!renaming && item.folder.id === newFolderId) {
                        setNewFolderId(null);
                      }
                    }}
                    onDrop={data => handleRootFolderDrop(data, item.folder.id)}
                    dropEffect={organizeChildrenDropEffect}
                    canDrop={handleRootChildrenCanDrop}
                    folderPermissionVariant="teamspace-folder"
                    location={{
                      at: 'navigation-panel:organize:folder-node',
                      nodeId: item.folder.id,
                    }}
                    parentPath={path}
                    hiddenDocIds={hiddenMailFolderDocIds}
                  />
                )
              )}
            </NavigationPanelTreeRoot>
          </NavigationPanelSidebarSortModeProvider>
        </div>
      </CollapsibleSection>
      {shareModalOpen ? (
        <FolderShareModal
          folderId={area.rootFolderId}
          variant="teamspace-root"
          open={shareModalOpen}
          onOpenChange={setShareModalOpen}
        />
      ) : null}
    </>
  );
};

type WorkspaceSidebarNavigationItem = {
  key: string;
  label: string;
  render: () => ReactElement | null;
};

const compareWorkspaceSidebarNavigationItems = (
  a: WorkspaceSidebarNavigationItem,
  b: WorkspaceSidebarNavigationItem
) => compareSidebarManualOrderItems(a, b);

const WorkspaceNavigationItems = ({
  includeKeys,
  labelOverrides,
  marketplaceRailTabs = false,
  onOpenSettingModal,
  reorderable = true,
  sessionStatus,
  showTooltips = false,
  storageKey,
}: {
  includeKeys?: string[];
  labelOverrides?: Partial<Record<'marketplace' | 'settings', string>>;
  marketplaceRailTabs?: boolean;
  onOpenSettingModal: () => void;
  reorderable?: boolean;
  sessionStatus: string | null | undefined;
  showTooltips?: boolean;
  storageKey: string;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const { isFeatureAllowed } = useFeatureGate();
  const canShowCalendar =
    workspace.flavour === 'local' ||
    isFeatureAllowed(FEATURE_KEY_DATABASE_CALENDAR);
  const canShowMail =
    workspace.flavour !== 'local' &&
    getWorkspaceMailMissingFeature(isFeatureAllowed) === null;
  const canShowWorkflows = isFeatureAllowed(FEATURE_KEY_WORKFLOW);
  const canShowMarketplace = isFeatureAllowed('marketplace-browse');
  const canShowCustomApps = useCanShowCustomAppsSidebarButton();
  const canShowGraphExplorer = useCanShowGraphExplorerSidebarButton();
  const canShowNotifications = sessionStatus === 'authenticated';
  const workspaceNavigationItems = useMemo<
    WorkspaceSidebarNavigationItem[]
  >(() => {
    const items: WorkspaceSidebarNavigationItem[] = [
      {
        key: 'all-docs',
        label: t['com.streamlain.workspaceSubPath.all'](),
        render: () => <AllDocsButton />,
      },
      {
        key: 'journals',
        label: t['com.streamlain.journal.app-sidebar-title'](),
        render: () => <AppSidebarJournalButton />,
      },
    ];

    if (canShowCalendar) {
      items.push({
        key: 'calendar',
        label: t['com.streamlain.rootAppSidebar.planner'](),
        render: () => <PlannerButton />,
      });
    }
    if (canShowMail) {
      items.push({
        key: 'mail',
        label: getMailAppLabel(t),
        render: () => <WorkspaceMailButton />,
      });
    }
    if (canShowNotifications) {
      items.push({
        key: 'notifications',
        label: t['com.streamlain.rootAppSidebar.notifications'](),
        render: () => <NotificationButton />,
      });
    }
    if (canShowCustomApps) {
      items.push({
        key: 'custom-apps',
        label: t['com.streamlain.customApps.title'](),
        render: () => <CustomAppsButton />,
      });
    }
    if (canShowGraphExplorer) {
      items.push({
        key: 'graph-explorer',
        label: t['com.streamlain.graph-explorer.title'](),
        render: () => <GraphExplorerButton />,
      });
    }
    if (canShowWorkflows) {
      items.push({
        key: 'workflows',
        label: t['com.streamlain.streamlain.workflow.page.title'](),
        render: () => <WorkflowsButton />,
      });
    }
    if (canShowMarketplace) {
      const marketplaceLabel =
        labelOverrides?.marketplace ??
        t['com.streamlain.marketplace.page.title']();
      items.push({
        key: 'marketplace',
        label: marketplaceLabel,
        render: () => (
          <MarketplaceButton
            label={marketplaceLabel}
            railTabs={marketplaceRailTabs}
          />
        ),
      });
    }
    const settingsLabel =
      labelOverrides?.settings ?? t['com.streamlain.settingSidebar.title']();
    items.push({
      key: 'settings',
      label: settingsLabel,
      render: () => (
        <MenuItem
          data-testid="slider-bar-workspace-setting-button"
          icon={<SettingsIcon />}
          onClick={onOpenSettingModal}
        >
          <span data-testid="settings-modal-trigger">{settingsLabel}</span>
        </MenuItem>
      ),
    });

    return items;
  }, [
    canShowCalendar,
    canShowCustomApps,
    canShowGraphExplorer,
    canShowMail,
    canShowMarketplace,
    canShowNotifications,
    canShowWorkflows,
    labelOverrides?.marketplace,
    labelOverrides?.settings,
    marketplaceRailTabs,
    onOpenSettingModal,
    t,
  ]);
  const {
    orderedItems: orderedWorkspaceNavigationItems,
    placeItem: placeWorkspaceNavigationItem,
  } = useSidebarManualOrder(
    storageKey,
    workspaceNavigationItems,
    compareWorkspaceSidebarNavigationItems,
    { defaultSortMode: 'manual', manualBase: 'source' }
  );
  const visibleWorkspaceNavigationItems = includeKeys
    ? includeKeys.flatMap(key => {
        const item = orderedWorkspaceNavigationItems.find(
          candidate => candidate.key === key
        );
        return item ? [item] : [];
      })
    : orderedWorkspaceNavigationItems;

  return (
    <>
      {visibleWorkspaceNavigationItems.map((item, index) => {
        const moveUp = () => {
          const before = visibleWorkspaceNavigationItems[index - 1];
          if (before) {
            placeWorkspaceNavigationItem(item.key, {
              at: 'before',
              key: before.key,
            });
          }
        };
        const moveDown = () => {
          const after = visibleWorkspaceNavigationItems[index + 1];
          if (after) {
            placeWorkspaceNavigationItem(item.key, {
              at: 'after',
              key: after.key,
            });
          }
        };
        const renderedItem = item.render();

        if (!renderedItem) {
          return null;
        }

        if (!reorderable) {
          return (
            <Tooltip
              content={showTooltips ? item.label : undefined}
              key={item.key}
              side="right"
            >
              <div className={railStyles.navigationTooltipTarget}>
                {renderedItem}
              </div>
            </Tooltip>
          );
        }

        return (
          <SidebarReorderableItem
            canMoveDown={index < visibleWorkspaceNavigationItems.length - 1}
            canMoveUp={index > 0}
            itemKey={item.key}
            key={item.key}
            label={item.label}
            onMoveDown={moveDown}
            onMoveUp={moveUp}
            onPlace={placeWorkspaceNavigationItem}
            scope="root-sidebar:workspace-navigation"
            testIdPrefix="workspace-navigation"
          >
            {renderedItem}
          </SidebarReorderableItem>
        );
      })}
    </>
  );
};

type OtherSidebarNavigationItem = {
  key: string;
  label: string;
  render: () => ReactElement | null;
};

const compareOtherSidebarNavigationItems = (
  a: OtherSidebarNavigationItem,
  b: OtherSidebarNavigationItem
) => compareSidebarManualOrderItems(a, b);

const OthersNavigationItems = ({
  canImport,
  includeKeys,
  labelOverrides,
  onOpenImportModal,
  reorderable = true,
  showTooltips = false,
  storageKey,
  trashCount,
}: {
  canImport: boolean;
  includeKeys?: string[];
  labelOverrides?: Partial<Record<string, string>>;
  onOpenImportModal: () => void;
  reorderable?: boolean;
  showTooltips?: boolean;
  storageKey: string;
  trashCount: number;
}) => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const canManageUsers = useGuard('Workspace_Users_Manage');
  const canInviteMembers =
    workspace.flavour !== 'local' && canManageUsers === true;
  const otherNavigationItems = useMemo<OtherSidebarNavigationItem[]>(
    () => [
      {
        key: 'trash',
        label: t['com.streamlain.workspaceSubPath.trash'](),
        render: () => (
          <TrashButton postfix={<SidebarBadge count={trashCount} />} />
        ),
      },
      {
        key: 'help',
        label: t['com.streamlain.help.menu'](),
        render: () => <HelpButton />,
      },
      ...(canImport
        ? [
            {
              key: 'import',
              label: t['Import'](),
              render: () => (
                <MenuItem
                  data-testid="slider-bar-import-button"
                  icon={<ImportIcon />}
                  onClick={onOpenImportModal}
                >
                  <span data-testid="import-modal-trigger">
                    {t['Import']()}
                  </span>
                </MenuItem>
              ),
            },
          ]
        : []),
      ...(canInviteMembers
        ? [
            {
              key: 'invite-members',
              label:
                labelOverrides?.['invite-members'] ?? t['Invite Members'](),
              render: () => (
                <InviteMembersButton
                  label={labelOverrides?.['invite-members']}
                />
              ),
            },
          ]
        : []),
    ],
    [
      canImport,
      canInviteMembers,
      labelOverrides,
      onOpenImportModal,
      t,
      trashCount,
    ]
  );
  const {
    orderedItems: orderedOtherNavigationItems,
    placeItem: placeOtherNavigationItem,
  } = useSidebarManualOrder(
    storageKey,
    otherNavigationItems,
    compareOtherSidebarNavigationItems,
    { defaultSortMode: 'manual', manualBase: 'source' }
  );
  const visibleOtherNavigationItems = includeKeys
    ? includeKeys.flatMap(key => {
        const item = orderedOtherNavigationItems.find(
          candidate => candidate.key === key
        );
        return item ? [item] : [];
      })
    : orderedOtherNavigationItems;

  return (
    <>
      {visibleOtherNavigationItems.map((item, index) => {
        const moveUp = () => {
          const before = visibleOtherNavigationItems[index - 1];
          if (before) {
            placeOtherNavigationItem(item.key, {
              at: 'before',
              key: before.key,
            });
          }
        };
        const moveDown = () => {
          const after = visibleOtherNavigationItems[index + 1];
          if (after) {
            placeOtherNavigationItem(item.key, {
              at: 'after',
              key: after.key,
            });
          }
        };
        const renderedItem = item.render();

        if (!renderedItem) {
          return null;
        }

        if (!reorderable) {
          return (
            <Tooltip
              content={showTooltips ? item.label : undefined}
              key={item.key}
              side="right"
            >
              <div className={railStyles.navigationTooltipTarget}>
                {renderedItem}
              </div>
            </Tooltip>
          );
        }

        return (
          <SidebarReorderableItem
            canMoveDown={index < visibleOtherNavigationItems.length - 1}
            canMoveUp={index > 0}
            itemKey={item.key}
            key={item.key}
            label={item.label}
            onMoveDown={moveDown}
            onMoveUp={moveUp}
            onPlace={placeOtherNavigationItem}
            scope="root-sidebar:others-navigation"
            testIdPrefix="others-navigation"
          >
            {renderedItem}
          </SidebarReorderableItem>
        );
      })}
    </>
  );
};

type DocumentsSidebarProps = {
  mobileOpen: boolean;
  onClose: () => void;
  open: boolean;
  twoPane: boolean;
};

export const DocumentsSidebar = memo(
  ({
    mobileOpen,
    onClose,
    open,
    twoPane,
  }: DocumentsSidebarProps): ReactElement => {
    const authService = useService(AuthService);
    const navigationPanelService = useService(NavigationPanelService);
    const navigationPanelRevealService = useService(
      NavigationPanelRevealService
    );
    const notificationCountService = useService(NotificationCountService);
    const appSidebarService = useService(AppSidebarService).sidebar;
    const workspace = useService(WorkspaceService).workspace;
    const workspaceDialogService = useService(WorkspaceDialogService);
    const workbench = useService(WorkbenchService).workbench;
    const accessibleDocCache = useService(AccessibleDocCacheStore);
    const accessibleFolderCache = useService(AccessibleFolderCacheStore);
    const sessionStatus = useLiveData(authService.session.status$);
    const account = useLiveData(authService.session.account$);
    const collectionsEnabled = useCollectionsFeatureEnabled();
    const tagsEnabled = useTagsFeatureEnabled();
    const t = useI18n();
    const { isFeatureAllowed } = useFeatureGate();
    const smallScreenMode = useLiveData(appSidebarService.smallScreenMode$);
    const workspaceNotificationCount = useLiveData(
      notificationCountService.totalCount$
    );
    const workspaceMailEnabled =
      workspace.flavour !== 'local' &&
      getWorkspaceMailMissingFeature(isFeatureAllowed) === null;
    const workspaceMailUnreadCount = useWorkspaceMailUnreadCount(
      workspaceMailEnabled ? workspace.id : null
    );
    const { appAreaRoot, canLoadAppArea } = useAppAreaSidebarAvailability();
    const appAreaVisible = canLoadAppArea && appAreaRoot !== null;
    const leftSidebar = useLeftSidebarController({
      accountId: account?.id ?? authService.cachedAccountId,
      appAreaVisible,
      collectionsEnabled,
      navigationPanelService,
      sessionStatus,
      smallScreenMode,
      tagsEnabled,
      workspaceId: workspace.id,
      workspaceMailEnabled,
    });
    const sidebarContentRef = useRef<HTMLDivElement | null>(null);
    const {
      personalAreaList: personalAreaListRef,
      personalSectionHeaderDropTarget: personalSectionHeaderDropTargetRef,
      teamAreasGroup: teamAreasGroupRef,
      teamAreasList: teamAreasListRef,
      teamAreasSectionHeaderDropTarget: teamAreasSectionHeaderDropTargetRef,
      workspaceExpandableList: workspaceExpandableListRef,
    } = leftSidebar.refs;
    const workspaceNavigationOrderStorageKey =
      leftSidebar.workspaceNavigationOrderStorageKey;
    const setSidebarSectionExpanded = leftSidebar.setSectionExpanded;
    const getMobileAwareTopGroupClassName = useCallback(
      (expanded: boolean, desktopClassName = sidebarMainSections) =>
        smallScreenMode
          ? expanded
            ? sidebarMobileActiveSectionGroup
            : sidebarMainSections
          : desktopClassName,
      [smallScreenMode]
    );
    const getMobileAwareSectionClassName = useCallback(
      (expanded: boolean, desktopClassName?: string) =>
        smallScreenMode && expanded ? sidebarMiddleSection : desktopClassName,
      [smallScreenMode]
    );
    const getMobileAwareContentClassName = useCallback(
      (expanded: boolean, desktopClassName?: string) =>
        smallScreenMode && expanded
          ? sidebarMiddleSectionContent
          : desktopClassName,
      [smallScreenMode]
    );
    const getMobileAwareFillLayout = useCallback(
      (expanded: boolean) =>
        smallScreenMode && expanded ? 'single-top' : undefined,
      [smallScreenMode]
    );
    const navigationRevealRequest = useLiveData(
      navigationPanelRevealService.request$
    );

    useEffect(() => {
      if (workspace.flavour === 'local') {
        return;
      }
      accessibleFolderCache.prime();
      accessibleDocCache.prime();
    }, [accessibleDocCache, accessibleFolderCache, workspace.flavour]);

    useEffect(() => {
      if (
        !navigationRevealRequest ||
        navigationRevealRequest.workspaceId !== workspace.id
      ) {
        return;
      }

      const sidebarContent = sidebarContentRef.current;
      if (!sidebarContent) {
        return;
      }

      const folderPath = navigationRevealRequest.docId
        ? getSidebarFolderPathForDoc(
            accessibleFolderCache,
            navigationRevealRequest.docId
          )
        : navigationRevealRequest.folderId
          ? getSidebarFolderPathForFolder(
              accessibleFolderCache,
              navigationRevealRequest.folderId
            )
          : null;
      if (folderPath) {
        const rootSection = getRootSidebarSectionForFolderPath(folderPath);
        if (rootSection) {
          setSidebarSectionExpanded(rootSection, true);
        }
        expandSidebarFolderPath(navigationPanelService, folderPath);
      }

      let cancelled = false;
      let animationFrame: number | null = null;
      let timeout: ReturnType<typeof setTimeout> | null = null;
      let mutationObserver: MutationObserver | null = null;
      let completed = false;

      const cancelScheduledWork = () => {
        if (animationFrame !== null) {
          cancelAnimationFrame(animationFrame);
          animationFrame = null;
        }
        if (timeout !== null) {
          clearTimeout(timeout);
          timeout = null;
        }
      };

      const completeRevealIfReady = () => {
        if (cancelled || completed) {
          return completed;
        }

        const target = navigationRevealRequest.docId
          ? findSidebarDocElement(sidebarContent, navigationRevealRequest.docId)
          : navigationRevealRequest.folderId
            ? findSidebarFolderElement(
                sidebarContent,
                navigationRevealRequest.folderId
              )
            : null;
        if (!target) {
          return false;
        }
        const renameSurfaceReady =
          !navigationRevealRequest.rename ||
          !navigationRevealRequest.docId ||
          sidebarContent.ownerDocument.querySelector(
            `[data-testid="rename-modal-input-${navigationRevealRequest.docId}"]`
          ) !== null;
        const rootSectionExpanded = isSidebarTargetRootSectionExpanded(target);
        if (
          !canCompleteNavigationPanelReveal({
            renameSurfaceReady,
            rootSectionExpanded,
          })
        ) {
          return false;
        }

        completed = true;
        mutationObserver?.disconnect();
        cancelScheduledWork();
        scrollSidebarElementIntoView(target);
        if (!navigationRevealRequest.openAfterRenameSubmit) {
          navigationPanelRevealService.clear(navigationRevealRequest.id);
        }
        return true;
      };

      const scheduleReveal = (attempt: number) => {
        cancelScheduledWork();
        animationFrame = requestAnimationFrame(() => {
          animationFrame = requestAnimationFrame(() => {
            if (cancelled || completeRevealIfReady()) {
              return;
            }

            if (attempt < 12) {
              timeout = setTimeout(() => scheduleReveal(attempt + 1), 80);
            } else if (!navigationRevealRequest.openAfterRenameSubmit) {
              navigationPanelRevealService.clear(navigationRevealRequest.id);
            }
          });
        });
      };

      mutationObserver = new MutationObserver(() => {
        if (!completeRevealIfReady()) {
          scheduleReveal(0);
        }
      });
      mutationObserver.observe(sidebarContent, {
        attributeFilter: ['data-expanded'],
        attributes: true,
        childList: true,
        subtree: true,
      });
      scheduleReveal(0);

      return () => {
        cancelled = true;
        mutationObserver?.disconnect();
        cancelScheduledWork();
      };
    }, [
      accessibleFolderCache,
      navigationPanelRevealService,
      navigationPanelService,
      navigationRevealRequest,
      setSidebarSectionExpanded,
      workspace.id,
    ]);

    const onOpenSettingModal = useCallback(() => {
      workspaceDialogService.open('setting', {
        activeTab: 'appearance',
      });
      track.$.navigationPanel.$.openSettings();
    }, [workspaceDialogService]);

    const handleNavigationRevealRenameFinish = useCallback(
      (requestId: number, reason: 'blur' | 'cancel' | 'submit') => {
        const request = navigationPanelRevealService.request$.value;
        if (!request || request.id !== requestId) {
          return;
        }
        navigationPanelRevealService.clear(requestId);
        if (
          reason === 'submit' &&
          request.openAfterRenameSubmit &&
          request.docId
        ) {
          workbench.openDoc(request.docId, { at: 'active' });
        }
      },
      [navigationPanelRevealService, workbench]
    );

    return (
      <DocumentsSidebarContent
        apps={
          appAreaVisible
            ? {
                children: <AppAreaSidebarContent />,
                scrollProps: {
                  onScrollTopChange: leftSidebar.scroll.apps.onScrollTopChange,
                  scrollRestoreKey: leftSidebar.scroll.apps.scrollRestoreKey,
                  scrollTop: leftSidebar.scroll.apps.scrollTop,
                },
                sectionProps: leftSidebar.sections.apps,
                title: t['com.streamlain.rootAppSidebar.appsSection'](),
              }
            : undefined
        }
        closeLabel={t['com.streamlain.streamlain.common.close']()}
        footer={
          <>
            <SidebarAudioPlayer />
            {LazyUpdaterButton ? (
              <Suspense fallback={null}>
                <LazyUpdaterButton />
              </Suspense>
            ) : null}
          </>
        }
        mobileOpen={mobileOpen}
        navigationRevealDocId={
          navigationRevealRequest?.workspaceId === workspace.id
            ? navigationRevealRequest.docId
            : undefined
        }
        navigationRevealRename={
          navigationRevealRequest?.workspaceId === workspace.id &&
          navigationRevealRequest.rename
        }
        navigationRevealRequestId={
          navigationRevealRequest?.workspaceId === workspace.id
            ? navigationRevealRequest.id
            : undefined
        }
        onNavigationRevealRenameFinish={handleNavigationRevealRenameFinish}
        onClose={onClose}
        open={open}
        personal={{
          action: <PersonalAddPageButton />,
          children: (
            <PersonalSidebarSections
              collectionsEnabled={collectionsEnabled}
              tagsEnabled={tagsEnabled}
            />
          ),
          fillLayout: getMobileAwareFillLayout(
            leftSidebar.sections.personal.expanded
          ),
          groupClassName: getMobileAwareTopGroupClassName(
            leftSidebar.sections.personal.expanded
          ),
          listRef: personalAreaListRef,
          scrollProps: {
            onScrollTopChange: leftSidebar.scroll.personal.onScrollTopChange,
            scrollRestoreKey: leftSidebar.scroll.personal.scrollRestoreKey,
            scrollSnap: 'proximity',
            scrollTop: leftSidebar.scroll.personal.scrollTop,
            snapScrollTop: leftSidebar.snapPersonalScrollTop,
          },
          sectionClassName: getMobileAwareSectionClassName(
            leftSidebar.sections.personal.expanded
          ),
          sectionContentClassName: getMobileAwareContentClassName(
            leftSidebar.sections.personal.expanded
          ),
          sectionHeaderRef: personalSectionHeaderDropTargetRef,
          sectionProps: leftSidebar.sections.personal,
          title: t['com.streamlain.rootAppSidebar.personal'](),
        }}
        rootRef={sidebarContentRef}
        teamAreas={{
          action: <TeamAreaAddPageButton />,
          children: <TeamAreaSections />,
          groupRef: teamAreasGroupRef,
          headerHeight: leftSidebar.sections.teamAreas.headerHeight,
          listRef: teamAreasListRef,
          scrollProps: {
            onScrollTopChange: leftSidebar.scroll.teamAreas.onScrollTopChange,
            scrollRestoreKey: leftSidebar.scroll.teamAreas.scrollRestoreKey,
            scrollSnap: 'proximity',
            scrollTop: leftSidebar.scroll.teamAreas.scrollTop,
            snapScrollTop: leftSidebar.snapTeamAreasScrollTop,
          },
          sectionHeaderRef: teamAreasSectionHeaderDropTargetRef,
          sectionProps: leftSidebar.sections.teamAreas,
          title: t['com.streamlain.rootAppSidebar.teamAreas'](),
        }}
        title={t['com.streamlain.rootAppSidebar.documents']()}
        twoPane={twoPane}
        workspace={{
          children: (
            <WorkspaceNavigationItems
              onOpenSettingModal={onOpenSettingModal}
              sessionStatus={sessionStatus}
              storageKey={workspaceNavigationOrderStorageKey}
            />
          ),
          collapsedSummary: (
            <WorkspaceSectionHeaderSummary
              mailCount={workspaceMailUnreadCount}
              notificationCount={
                sessionStatus === 'authenticated'
                  ? workspaceNotificationCount
                  : 0
              }
            />
          ),
          fillLayout: getMobileAwareFillLayout(
            leftSidebar.sections.workspace.expanded
          ),
          groupClassName: getMobileAwareTopGroupClassName(
            leftSidebar.sections.workspace.expanded
          ),
          listRef: workspaceExpandableListRef,
          scrollProps: {
            onScrollTopChange: leftSidebar.scroll.workspace.onScrollTopChange,
            scrollRestoreKey: leftSidebar.scroll.workspace.scrollRestoreKey,
            scrollSnap: 'proximity',
            scrollTop: leftSidebar.scroll.workspace.scrollTop,
            snapScrollTop: leftSidebar.snapWorkspaceScrollTop,
          },
          sectionClassName: getMobileAwareSectionClassName(
            leftSidebar.sections.workspace.expanded
          ),
          sectionContentClassName: getMobileAwareContentClassName(
            leftSidebar.sections.workspace.expanded
          ),
          sectionProps: leftSidebar.sections.workspace,
          title: t['com.streamlain.rootAppSidebar.home'](),
        }}
      />
    );
  }
);

DocumentsSidebar.displayName = 'memo(DocumentsSidebar)';

const FavoritesNavigationOverlay = memo(
  ({
    mobileOpen,
    onClose,
    open,
  }: {
    mobileOpen: boolean;
    onClose: () => void;
    open: boolean;
  }): ReactElement => {
    const t = useI18n();

    return (
      <NavigationOverlayPane
        collapseLabel={t['com.streamlain.cmdk.affine.left-sidebar.collapse']()}
        closeLabel={t['com.streamlain.streamlain.common.close']()}
        headerControl="collapse"
        headerClassName={favoritesOverlayHeader}
        mobileOpen={mobileOpen}
        onClose={onClose}
        open={open}
        paneClassName={favoritesOverlaySurface}
        testId="global-navigation-favorites-panel"
        title={
          <span className={navigationOverlayTitle}>
            <FavoriteIcon className={navigationOverlayTitleIcon} />
            <span className={navigationOverlayTitleText}>
              {t['com.streamlain.rootAppSidebar.favorites']()}
            </span>
          </span>
        }
      >
        <SidebarScrollableContainer
          className={sidebarMiddleScroll}
          contentPadding="none"
        >
          <div className={sidebarOrganization}>
            <NavigationPanelTreeSelectionProvider>
              <div className={clsx(organizeSections, favoritesOverlaySections)}>
                <NavigationPanelFavorites presentation="panel" />
              </div>
              <NavigationPanelTreeSelectionToolbar />
            </NavigationPanelTreeSelectionProvider>
          </div>
        </SidebarScrollableContainer>
      </NavigationOverlayPane>
    );
  }
);

FavoritesNavigationOverlay.displayName = 'memo(FavoritesNavigationOverlay)';

/**
 * This is for the whole affine app sidebar.
 * This component wraps the app sidebar in `@affine/component` with logic and data.
 *
 */
export const RootAppSidebar = memo((): ReactElement => {
  const { workbenchService, cMDKQuickSearchService, authService } = useServices(
    {
      WorkbenchService,
      CMDKQuickSearchService,
      AuthService,
    }
  );

  const sessionStatus = useLiveData(authService.session.status$);
  const account = useLiveData(authService.session.account$);
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const tagsEnabled = useTagsFeatureEnabled();
  const t = useI18n();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const navigationPanelRevealService = useService(NavigationPanelRevealService);
  const workspace = useService(WorkspaceService).workspace;
  const organizeService = useService(OrganizeService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const folderTreeLoading = useLiveData(organizeService.folderTree.isLoading$);
  const folderCachePopulated = useLiveData(accessibleFolderCache.isPopulated$);
  const folderLocalCacheLoaded = useLiveData(
    accessibleFolderCache.isLocalCacheLoaded$
  );
  const docCachePopulated = useLiveData(accessibleDocCache.isPopulated$);
  const docLocalCacheLoaded = useLiveData(
    accessibleDocCache.isLocalCacheLoaded$
  );
  const { isLocalEntitlementReady } = useFeatureGate();
  const isCloudWorkspace = workspace.flavour !== 'local';
  const folderCacheReady = folderLocalCacheLoaded || folderCachePopulated;
  const docCacheReady = docLocalCacheLoaded || docCachePopulated;
  const appShellReady = isCloudWorkspace
    ? folderCacheReady && docCacheReady && isLocalEntitlementReady
    : !folderTreeLoading;
  useBootSplashReady(appShellReady);
  useEffect(() => {
    if (isCloudWorkspace) {
      accessibleFolderCache.prime();
      accessibleDocCache.prime();
    }
  }, [accessibleDocCache, accessibleFolderCache, isCloudWorkspace]);
  useEffect(() => {
    recordBootEvent('app-shell-readiness-state', {
      docCachePopulated,
      docLocalCacheLoaded,
      folderCachePopulated,
      folderLocalCacheLoaded,
      folderTreeLoading,
      isLocalEntitlementReady,
      ready: appShellReady,
      workspaceType: isCloudWorkspace ? 'cloud' : 'local',
    });
    if (!isCloudWorkspace || appShellReady) return;
    if (!folderCacheReady) {
      setBootBlocker('folder-cache-local');
    } else if (!docCacheReady) {
      setBootBlocker('doc-cache-local');
    } else if (!isLocalEntitlementReady) {
      setBootBlocker('feature-entitlement-local');
    }
  }, [
    appShellReady,
    docCachePopulated,
    docCacheReady,
    docLocalCacheLoaded,
    folderCachePopulated,
    folderCacheReady,
    folderLocalCacheLoaded,
    folderTreeLoading,
    isCloudWorkspace,
    isLocalEntitlementReady,
  ]);
  const trashCount = useSidebarTrashCount();
  const canCreateDoc = useGuard('Workspace_CreateDoc');
  const canWriteBlobs = useGuard('Workspace_Blobs_Write');
  const canImport =
    workspace.flavour === 'local' ||
    (canCreateDoc === true && canWriteBlobs === true);
  const workbench = workbenchService.workbench;
  const workspaceSelectorOpen = useLiveData(workbench.workspaceSelectorOpen$);
  const navigationRevealRequest = useLiveData(
    navigationPanelRevealService.request$
  );
  const sidebarUiStateStorageKey = useMemo(
    () =>
      createRootAppSidebarResolvedUiStateStorageKey(
        account?.id ?? authService.cachedAccountId,
        workspace.id
      ),
    [account?.id, authService.cachedAccountId, workspace.id]
  );
  const workspaceNavigationOrderStorageKey = sidebarUiStateStorageKey
    ? `${sidebarUiStateStorageKey}:workspace-navigation-order`
    : `streamlain:rootAppSidebar.workspaceNavigationOrder:${workspace.id}`;
  const othersNavigationOrderStorageKey = sidebarUiStateStorageKey
    ? `${sidebarUiStateStorageKey}:others-navigation-order`
    : `streamlain:rootAppSidebar.othersNavigationOrder:${workspace.id}`;
  const performanceInputs = {
    accountId: account?.id ?? null,
    canCreateDoc,
    canImport,
    canWriteBlobs,
    collectionsEnabled,
    navigationRevealRequestId: navigationRevealRequest?.id ?? null,
    sessionStatus,
    tagsEnabled,
    workspaceSelectorOpen,
    trashCount,
  };
  const previousPerformanceInputs = useRef(performanceInputs);
  useEffect(() => {
    const changed = Object.keys(performanceInputs).filter(
      key =>
        !Object.is(
          previousPerformanceInputs.current[
            key as keyof typeof performanceInputs
          ],
          performanceInputs[key as keyof typeof performanceInputs]
        )
    );
    if (changed.length === 0) {
      markSidebarPerformanceSignal('root-render:untracked-input');
    } else {
      changed.forEach(key => markSidebarPerformanceSignal(`root-input:${key}`));
    }
    previousPerformanceInputs.current = performanceInputs;
  });

  const onOpenQuickSearchModal = useCallback(() => {
    cMDKQuickSearchService.toggle({
      collectionsEnabled,
      tagsEnabled,
    });
  }, [cMDKQuickSearchService, collectionsEnabled, tagsEnabled]);

  const onWorkspaceSelectorOpenChange = useCallback(
    (open: boolean) => {
      workbench.setWorkspaceSelectorOpen(open);
    },
    [workbench]
  );

  const onOpenSettingModal = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'appearance',
    });
    track.$.navigationPanel.$.openSettings();
  }, [workspaceDialogService]);

  const handleOpenDocs = useCallback(
    (result: {
      docIds: string[];
      entryId?: string;
      isWorkspaceFile?: boolean;
    }) => {
      const { docIds, entryId, isWorkspaceFile } = result;
      // If the imported file is a workspace file, open the entry page.
      if (isWorkspaceFile && entryId) {
        workbench.openDoc(entryId);
      } else if (!docIds.length) {
        return;
      }
      // Open all the docs when there are multiple docs imported.
      if (docIds.length > 1) {
        workbench.openAll();
      } else {
        // Otherwise, open the only doc.
        workbench.openDoc(docIds[0]);
      }
    },
    [workbench]
  );

  const onOpenImportModal = useCallback(() => {
    track.$.navigationPanel.importModal.open();
    workspaceDialogService.open('import', undefined, payload => {
      if (!payload) {
        return;
      }
      handleOpenDocs(payload);
    });
  }, [workspaceDialogService, handleOpenDocs]);

  const currentPathname = useLiveData(
    workbench.location$.selector(location => location.pathname)
  );
  const allPageActive = currentPathname === '/all';
  const [activeNavigationOverlay, setActiveNavigationOverlay] = useState<
    'favorites' | null
  >(null);
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const mobileRailViewport = useNavigationRailViewport(
    MOBILE_NAVIGATION_RAIL_QUERY
  );
  const tabletRailViewport = useNavigationRailViewport(
    TABLET_NAVIGATION_RAIL_QUERY
  );
  const mobileRailVisible = !mobileRailViewport || mobileRailOpen;

  useEffect(() => {
    setActiveNavigationOverlay(null);
  }, [currentPathname]);

  useEffect(() => {
    if (!mobileRailViewport) {
      setMobileRailOpen(false);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveNavigationOverlay(null);
        setMobileRailOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileRailViewport]);

  useEffect(() => {
    if (
      navigationRevealRequest?.workspaceId === workspace.id &&
      (navigationRevealRequest.docId || navigationRevealRequest.folderId)
    ) {
      requestNativeWorkspaceAppSidebar('workspace', 'open');
      if (mobileRailViewport) {
        setMobileRailOpen(true);
      }
    }
  }, [mobileRailViewport, navigationRevealRequest, workspace.id]);

  useEffect(() => {
    if (allPageActive) {
      requestNativeWorkspaceAppSidebar('workspace', 'open');
    }
  }, [allPageActive]);

  const mobileRailLabel = mobileRailOpen
    ? t['com.streamlain.cmdk.affine.left-sidebar.collapse']()
    : t['com.streamlain.cmdk.affine.left-sidebar.expand']();

  return (
    <>
      <AppSidebar staticRail>
        <div className={railStyles.shell} data-mobile-open={mobileRailVisible}>
          <button
            aria-label={mobileRailLabel}
            className={railStyles.mobileBackdrop}
            data-open={mobileRailOpen}
            onClick={() => {
              setActiveNavigationOverlay(null);
              setMobileRailOpen(false);
            }}
            tabIndex={mobileRailOpen ? 0 : -1}
            type="button"
          />
          <div
            className={railStyles.rail}
            data-mobile-open={mobileRailVisible}
            data-testid="global-navigation-rail"
            id="global-navigation-rail"
          >
            <div className={railStyles.workspaceSlot}>
              <WorkspaceNavigator
                showEnableCloudButton
                showSyncStatus
                open={workspaceSelectorOpen}
                onOpenChange={onWorkspaceSelectorOpenChange}
                dense
              />
            </div>
            <div className={railStyles.searchSection}>
              <div className={railStyles.searchSlot}>
                <QuickSearchInput
                  data-testid="slider-bar-quick-search-button"
                  data-event-props="$.navigationPanel.$.quickSearch"
                  onClick={onOpenQuickSearchModal}
                  showTooltip={tabletRailViewport}
                />
              </div>
              <div className={railStyles.searchDivider} />
            </div>
            <SidebarScrollableContainer
              className={railStyles.scrollRegion}
              contentPadding="none"
              scrollbarVisibility="scroll"
            >
              <div className={railStyles.scrollContent}>
                <Tooltip
                  content={
                    tabletRailViewport
                      ? t['com.streamlain.rootAppSidebar.favorites']()
                      : undefined
                  }
                  side="right"
                >
                  <button
                    aria-expanded={activeNavigationOverlay === 'favorites'}
                    aria-label={t['com.streamlain.rootAppSidebar.favorites']()}
                    className={railStyles.docsButton}
                    data-active={activeNavigationOverlay === 'favorites'}
                    data-testid="global-navigation-favorites-button"
                    onClick={() =>
                      setActiveNavigationOverlay(active =>
                        active === 'favorites' ? null : 'favorites'
                      )
                    }
                    type="button"
                  >
                    <FavoriteIcon />
                    <span>
                      {t['com.streamlain.rootAppSidebar.favorites']()}
                    </span>
                  </button>
                </Tooltip>
                <NavigationRailGroup
                  active={allPageActive}
                  ariaLabel={t['com.streamlain.rootAppSidebar.workspace']()}
                  revealWhenPopulated
                  testId="global-navigation-document-modes"
                  primary={
                    <Tooltip
                      content={
                        tabletRailViewport
                          ? t['com.streamlain.rootAppSidebar.workspace']()
                          : undefined
                      }
                      side="right"
                    >
                      <button
                        aria-label={t[
                          'com.streamlain.rootAppSidebar.workspace'
                        ]()}
                        aria-current={allPageActive ? 'page' : undefined}
                        className={railStyles.docsButton}
                        data-active={allPageActive}
                        data-testid="global-navigation-docs-button"
                        onClick={() => {
                          setActiveNavigationOverlay(null);
                          if (!allPageActive) {
                            workbench.openAll();
                          }
                        }}
                        type="button"
                      >
                        <TeamWorkspaceIcon />
                        <span>
                          {t['com.streamlain.rootAppSidebar.workspace']()}
                        </span>
                      </button>
                    </Tooltip>
                  }
                >
                  <DocumentRailModeTarget
                    className={railStyles.documentModeTarget}
                  />
                </NavigationRailGroup>
                <WorkspaceNavigationItems
                  includeKeys={[
                    'notifications',
                    'mail',
                    'calendar',
                    'journals',
                    'workflows',
                    'custom-apps',
                    'marketplace',
                    'graph-explorer',
                  ]}
                  labelOverrides={{
                    marketplace: t['com.streamlain.rootAppSidebar.hub'](),
                  }}
                  marketplaceRailTabs
                  onOpenSettingModal={onOpenSettingModal}
                  reorderable={false}
                  sessionStatus={sessionStatus}
                  showTooltips={tabletRailViewport}
                  storageKey={workspaceNavigationOrderStorageKey}
                />
                <div
                  className={railStyles.bottomNavigationGroup}
                  data-testid="global-navigation-extras"
                >
                  <div className={railStyles.partialDivider} />
                  <OthersNavigationItems
                    canImport={canImport}
                    includeKeys={['invite-members', 'import', 'trash', 'help']}
                    labelOverrides={{
                      'invite-members':
                        t['com.streamlain.rootAppSidebar.members'](),
                    }}
                    onOpenImportModal={onOpenImportModal}
                    reorderable={false}
                    showTooltips={tabletRailViewport}
                    storageKey={othersNavigationOrderStorageKey}
                    trashCount={trashCount}
                  />
                </div>
              </div>
            </SidebarScrollableContainer>
            <div className={railStyles.footerSection}>
              <div className={railStyles.footerDivider} />
              <div className={railStyles.footer}>
                <div className={railStyles.avatarSlot}>
                  <div className={railStyles.avatarControl}>
                    <UserInfo />
                  </div>
                  <SidebarConnectivityStatus placement="rail-avatar" />
                </div>
                <WorkspaceNavigationItems
                  includeKeys={['settings']}
                  labelOverrides={{
                    settings: t['com.streamlain.rootAppSidebar.settings'](),
                  }}
                  onOpenSettingModal={onOpenSettingModal}
                  reorderable={false}
                  sessionStatus={sessionStatus}
                  showTooltips={tabletRailViewport}
                  storageKey={workspaceNavigationOrderStorageKey}
                />
              </div>
            </div>
          </div>
          <FavoritesNavigationOverlay
            mobileOpen={mobileRailVisible}
            onClose={() => setActiveNavigationOverlay(null)}
            open={activeNavigationOverlay === 'favorites'}
          />
          <button
            aria-controls="global-navigation-rail"
            aria-expanded={mobileRailOpen}
            aria-label={mobileRailLabel}
            className={railStyles.mobileRailLauncher}
            data-open={mobileRailOpen}
            onClick={() =>
              setMobileRailOpen(open => {
                if (open) {
                  setActiveNavigationOverlay(null);
                }
                return !open;
              })
            }
            type="button"
          >
            <SidebarIcon />
          </button>
        </div>
      </AppSidebar>
    </>
  );
});

RootAppSidebar.displayName = 'memo(RootAppSidebar)';
